/* eslint-disable */
import type { Money } from "../../../common/currency.pb";
import type { BrowserInteraction } from "../../../franklin/browser_interaction.pb";
import type { ScenarioInitiatorType } from "../../../franklin/common/scenario_initiator_type.pb";
import type { Region } from "../../../franklin/region.pb";
import type { BlockerDescriptorTheme } from "../../../franklin/scenarios.pb";
import type { CardRequestInfo } from "../../../rewardly/common.pb";
import type { BirthdateEndResult } from "../../cashidv/common/Birthdate.pb";
import type { Image } from "../../ui/Image.pb";
import type { PasswordCreationDisplayConfig, PasswordVerificationDisplayConfig } from "../common/Passwords.pb";
import type { SemanticTypes } from "../common/SemanticTypes.pb";
import type { HumanConfirmationParameters } from "../kgoose/HumanConfirmationParameters.pb";

export interface Flow {
  /**
   * Identifier of the flow, defined by clients.
   *
   * NOTE: THIS SHOULD NOT CONTAIN PII AS IT'S LOGGED AND DISPLAYED IN ADMIN TOOLING.
   */
  token?: string;
  customer_token?: string;
  /** Type of a flow. String value of the enums found at Type. */
  type?: string;
  parameters?: FlowParameters;
  intent?: FlowFlowIntent;
}

/** DO NOT RENAME ENUM VALUES EVEN WHEN TAG IS THE SAME. */
export enum FlowType {
  /** ORDER_PIZZA_FLOW - ORDER_PIZZA_FLOW and EXCEPTION_FLOW flows are for testing. */
  ORDER_PIZZA_FLOW = "ORDER_PIZZA_FLOW",
  EXCEPTION_FLOW = "EXCEPTION_FLOW",
  RECIPIENT_PAYMENT_FLOW = "RECIPIENT_PAYMENT_FLOW",
  /** REMOVE_BOOST - Removes selected Boost from the card. */
  REMOVE_BOOST = "REMOVE_BOOST",
  SENDER_PAYMENT_FLOW = "SENDER_PAYMENT_FLOW",
  /** ADD_BOOST - Adds or replaces an existing Boost on the card. */
  ADD_BOOST = "ADD_BOOST",
  /** ONBOARDING - Onboarding flow */
  ONBOARDING = "ONBOARDING",
  /** LOCK_ISSUED_CARD - Lock customer's primary issued card. */
  LOCK_ISSUED_CARD = "LOCK_ISSUED_CARD",
  /**
   * DISABLE_ISSUED_CARD - Disable a customer's primary issued card.
   *
   * @deprecated
   */
  DISABLE_ISSUED_CARD = "DISABLE_ISSUED_CARD",
  /** CHANGE_PASSCODE - Flow to change a customer's passcode. */
  CHANGE_PASSCODE = "CHANGE_PASSCODE",
  /** ACTIVATE_PHYSICAL_CARD - Activate physical card. */
  ACTIVATE_PHYSICAL_CARD = "ACTIVATE_PHYSICAL_CARD",
  /** @deprecated */
  REPORT_CARD_LOST_OR_STOLEN = "REPORT_CARD_LOST_OR_STOLEN",
  CHANGE_CARD_DESIGN = "CHANGE_CARD_DESIGN",
  REQUEST_PHYSICAL_CARD = "REQUEST_PHYSICAL_CARD",
  /**
   * IDENTITY_VERIFICATION - Any new product requirement should use the IDV_IDENTITY_VERIFICATION flow instead.
   *
   * @deprecated
   */
  IDENTITY_VERIFICATION = "IDENTITY_VERIFICATION",
  AMEND_CARD_MAILING_ADDRESS = "AMEND_CARD_MAILING_ADDRESS",
  LINK_PHONE_NUMBER = "LINK_PHONE_NUMBER",
  LINK_EMAIL_ADDRESS = "LINK_EMAIL_ADDRESS",
  HEAVY_CUSTOMER = "HEAVY_CUSTOMER",
  /** INITIATE_CRYPTO_PAYMENT - Flow to initiate the sending of a crypto (bitcoin) payment */
  INITIATE_CRYPTO_PAYMENT = "INITIATE_CRYPTO_PAYMENT",
  /**
   * THIRD_PARTY_AUTHORIZATION - Authorization flow with a third party.
   *
   * @deprecated
   */
  THIRD_PARTY_AUTHORIZATION = "THIRD_PARTY_AUTHORIZATION",
  MOBILE_CHECK_DEPOSIT = "MOBILE_CHECK_DEPOSIT",
  /** DISPUTE_CARD_TRANSACTION - No longer used */
  DISPUTE_CARD_TRANSACTION = "DISPUTE_CARD_TRANSACTION",
  SET_JURISDICTION = "SET_JURISDICTION",
  /** GET_DIRECT_DEPOSIT_FORM - Flow to generate and expose a PDF Direct Deposit Form. */
  GET_DIRECT_DEPOSIT_FORM = "GET_DIRECT_DEPOSIT_FORM",
  REPORT_LOST_CARD = "REPORT_LOST_CARD",
  /** @deprecated */
  ACTIVATE_PHYSICAL_CARD_IN_POSTCARD = "ACTIVATE_PHYSICAL_CARD_IN_POSTCARD",
  /** @deprecated */
  PEERMENTS_SENDER_FLOW = "PEERMENTS_SENDER_FLOW",
  /** ENABLE_ISSUED_CARD - Enable a customer's primary issued card. */
  ENABLE_ISSUED_CARD = "ENABLE_ISSUED_CARD",
  /** DISABLE_ISSUED_CARD_IN_POSTCARD - Disable a customer's primary issued card. */
  DISABLE_ISSUED_CARD_IN_POSTCARD = "DISABLE_ISSUED_CARD_IN_POSTCARD",
  /** GET_EVENT_BASED_BOOST_DETAIL - Flow to retrieve details on event based boost. */
  GET_EVENT_BASED_BOOST_DETAIL = "GET_EVENT_BASED_BOOST_DETAIL",
  /** UNLOCK_ISSUED_CARD - Unlock customer's primary issued card. */
  UNLOCK_ISSUED_CARD = "UNLOCK_ISSUED_CARD",
  /** ACCEPT_CRYPTO_PAYMENT - Flow to kick off receiving a crypto (bitcoin) payment */
  ACCEPT_CRYPTO_PAYMENT = "ACCEPT_CRYPTO_PAYMENT",
  ACTIVATE_DIRECT_DEPOSIT_ACCOUNT = "ACTIVATE_DIRECT_DEPOSIT_ACCOUNT",
  UNLOCK_QR_CODE_BOOST = "UNLOCK_QR_CODE_BOOST",
  /**
   * FRANKLIN_INVEST_EQUITY_CUSTOMER_ONBOARDING - Replaced by INVEST_EQUITY_CUSTOMER_ONBOARDING.
   *
   * @deprecated
   */
  FRANKLIN_INVEST_EQUITY_CUSTOMER_ONBOARDING = "FRANKLIN_INVEST_EQUITY_CUSTOMER_ONBOARDING",
  /** DDA_DETAILS - Flow to show DDA account details */
  DDA_DETAILS = "DDA_DETAILS",
  /** GOOGLE_PAY_PROVISIONING - Flow to provision customer's card with Google Pay in the app to app case */
  GOOGLE_PAY_PROVISIONING = "GOOGLE_PAY_PROVISIONING",
  TRANSFER_FUNDS = "TRANSFER_FUNDS",
  /**
   * ACCEPT_INVEST_PAYMENT - [DEPRECATED] Flow to kick off receiving an invest payment
   * Stock gifting has been removed from both iOS and Android from app version 4.92 onwards
   *
   * @deprecated
   */
  ACCEPT_INVEST_PAYMENT = "ACCEPT_INVEST_PAYMENT",
  /** EDIT_PROFILE_BIO - Flow to edit your bio. */
  EDIT_PROFILE_BIO = "EDIT_PROFILE_BIO",
  /** REPORT_PROFILE - Flow to report a profile. */
  REPORT_PROFILE = "REPORT_PROFILE",
  /** DECLINE_CRYPTO_PAYMENT - Flow to kick off declining a crypto (bitcoin) payment */
  DECLINE_CRYPTO_PAYMENT = "DECLINE_CRYPTO_PAYMENT",
  /**
   * SEND_INVEST_PAYMENT - [DEPRECATED] Flow to initiate sending an invest payment
   * Stock gifting has been removed from both iOS and Android from app version 4.92 onwards
   *
   * @deprecated
   */
  SEND_INVEST_PAYMENT = "SEND_INVEST_PAYMENT",
  /**
   * IDV_IDENTITY_VERIFICATION - NO NEW CALLERS ALLOWED. Must go through eligibility in order to call IDV
   * https://dev-guides.sqprod.co/cash/docs/safeguards/eligibility/integration
   */
  IDV_IDENTITY_VERIFICATION = "IDV_IDENTITY_VERIFICATION",
  /**
   * INVEST_EQUITY_TRADE - Deprecated, use INVEST_EQUITY_TRADE_BUY and INVEST_EQUITY_TRADE_SELL
   * Flow for equity trades (BUY, SELL)
   *
   * @deprecated
   */
  INVEST_EQUITY_TRADE = "INVEST_EQUITY_TRADE",
  /** DEPOSIT_PAPER_CASH - Flow to deposit paper cash */
  DEPOSIT_PAPER_CASH = "DEPOSIT_PAPER_CASH",
  /** GET_BANKING_INTERSTITIAL - Flow to show features of Cash card from the Banking tab. */
  GET_BANKING_INTERSTITIAL = "GET_BANKING_INTERSTITIAL",
  /** TRANSFER_FROM_BANKS - Flow to show how customers can transfer money from other banks. */
  TRANSFER_FROM_BANKS = "TRANSFER_FROM_BANKS",
  /** CARD_EXPIRATION - Flow to notify customer their card is expiring and to reorder card. */
  CARD_EXPIRATION = "CARD_EXPIRATION",
  /** REQUEST_SPONSORSHIP - A flow for an under-18 account to request sponsorship from an adult. */
  REQUEST_SPONSORSHIP = "REQUEST_SPONSORSHIP",
  /** APPROVE_SPONSORSHIP - A flow for an adult to approve a sponsorship request from an under-18 account. */
  APPROVE_SPONSORSHIP = "APPROVE_SPONSORSHIP",
  /** INITIATE_TAX_UPGRADE - Flow to initiate tax upgrade/login */
  INITIATE_TAX_UPGRADE = "INITIATE_TAX_UPGRADE",
  /** DEPOSIT_PAPER_CASH_CONFIRMATION - Flow to confirm the deposit of paper cash */
  DEPOSIT_PAPER_CASH_CONFIRMATION = "DEPOSIT_PAPER_CASH_CONFIRMATION",
  /** ELECTRONIC_IDENTITY_VERIFICATION - Flow to perform electronic identity verification. */
  ELECTRONIC_IDENTITY_VERIFICATION = "ELECTRONIC_IDENTITY_VERIFICATION",
  /** GET_INSTANT_PAYOUT - Flow to allow customers to get their paycheck on demand */
  GET_INSTANT_PAYOUT = "GET_INSTANT_PAYOUT",
  /** UNLOCK_BANKING_DEPOSIT_OPTIONS - Flow to unlock banking deposit options. */
  UNLOCK_BANKING_DEPOSIT_OPTIONS = "UNLOCK_BANKING_DEPOSIT_OPTIONS",
  /** INITIATE_CRYPTO_WITHDRAWAL - Flow to initiate a crypto (bitcoin) withdrawal to the blockchain */
  INITIATE_CRYPTO_WITHDRAWAL = "INITIATE_CRYPTO_WITHDRAWAL",
  /** CLOSE_ACCOUNT - Flow to close a customer's account */
  CLOSE_ACCOUNT = "CLOSE_ACCOUNT",
  /** ENTER_QR_MARKETING_EVENT - Flow to enter a marketing event by scanning a QR code. */
  ENTER_QR_MARKETING_EVENT = "ENTER_QR_MARKETING_EVENT",
  /** SET_OR_UPDATE_PASSWORD - Flow to create a password or update it if it already exists. */
  SET_OR_UPDATE_PASSWORD = "SET_OR_UPDATE_PASSWORD",
  /** SET_OR_VERIFY_PASSWORD - Flow to verify a user's password or prompt to create one if it doesn't exist. */
  SET_OR_VERIFY_PASSWORD = "SET_OR_VERIFY_PASSWORD",
  /**
   * INITIATE_GLOBAL_PAYMENT - Flow to initiate a payment between customers of two different local networks
   * participating in the Network of Networks.
   */
  INITIATE_GLOBAL_PAYMENT = "INITIATE_GLOBAL_PAYMENT",
  /** INITIATE_CRYPTO_DEPOSIT_REVERSAL - Flow to initiate a crypto (bitcoin) deposit reversal (withdrawal) to an external wallet */
  INITIATE_CRYPTO_DEPOSIT_REVERSAL = "INITIATE_CRYPTO_DEPOSIT_REVERSAL",
  /** REQUIRE_NON_VOIP_PHONE_NUMBER - Flow to require that a user has a recently confirmed, non-voip phone number. */
  REQUIRE_NON_VOIP_PHONE_NUMBER = "REQUIRE_NON_VOIP_PHONE_NUMBER",
  /** INVEST_CRYPTO_CUSTOMER_ONBOARDING - Manages the customer onboarding flow for crypto investing */
  INVEST_CRYPTO_CUSTOMER_ONBOARDING = "INVEST_CRYPTO_CUSTOMER_ONBOARDING",
  /** ACCEPT_TERMS_OF_SERVICE_AGREEMENT - Flow to display and allow user to accept terms of service agreement */
  ACCEPT_TERMS_OF_SERVICE_AGREEMENT = "ACCEPT_TERMS_OF_SERVICE_AGREEMENT",
  /** RECOVER_PASSWORD - Flow to reset a user's password */
  RECOVER_PASSWORD = "RECOVER_PASSWORD",
  /** DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL - Flow to switch customer direct deposit via payroll login. */
  DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL = "DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL",
  /**
   * IDV_OR_SPONSORSHIP - NO NEW CALLERS ALLOWED. Must go through eligibility in order to call IDV
   * https://dev-guides.sqprod.co/cash/docs/safeguards/eligibility/integration
   */
  IDV_OR_SPONSORSHIP = "IDV_OR_SPONSORSHIP",
  /** CARD_LSB_REORDER - A flow to notify customer their shazam card is about to terminate and to reorder card. */
  CARD_LSB_REORDER = "CARD_LSB_REORDER",
  /** RESEND_SPONSORSHIP_REQUEST - A flow to resend a sponsorship request. */
  RESEND_SPONSORSHIP_REQUEST = "RESEND_SPONSORSHIP_REQUEST",
  /** INITIATE_LIGHTNING_WITHDRAWAL - Flow to initiate a lightning withdrawal */
  INITIATE_LIGHTNING_WITHDRAWAL = "INITIATE_LIGHTNING_WITHDRAWAL",
  /** NEW_TO_BOOST_INFORMATION - A flow to provide Boost information for customers who have not interacted with Boosts. */
  NEW_TO_BOOST_INFORMATION = "NEW_TO_BOOST_INFORMATION",
  /** SEND_PRINT_CHECK - HACKWEEK - A flow to allow customers to send checks from their account * */
  SEND_PRINT_CHECK = "SEND_PRINT_CHECK",
  /** COLLECT_ADDRESS - flow to collect addresses as part of IDV. */
  COLLECT_ADDRESS = "COLLECT_ADDRESS",
  /** TOTP - Flow for TOTP */
  TOTP = "TOTP",
  /** DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL_CONFIRMATION - Flow to confirm switching customer's direct deposit via payroll. */
  DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL_CONFIRMATION = "DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL_CONFIRMATION",
  /** REQUIRE_SECOND_FACTOR_VERIFICATION - Flow to choose between non-voip phone verification vs TOTP based on customer settings */
  REQUIRE_SECOND_FACTOR_VERIFICATION = "REQUIRE_SECOND_FACTOR_VERIFICATION",
  /** CRYPTO_INVEST_CURRENCY_EXCHANGE - Flow to initiate exchange between FIAT currency and bitcoin in US */
  CRYPTO_INVEST_CURRENCY_EXCHANGE = "CRYPTO_INVEST_CURRENCY_EXCHANGE",
  /** DD_SWITCH_UPSELL_CARD_ACTIVATED - Flow for upsell direct deposit switch at the end of a successful card activation. */
  DD_SWITCH_UPSELL_CARD_ACTIVATED = "DD_SWITCH_UPSELL_CARD_ACTIVATED",
  /** REVIEW_LOGIN - Flow to allow customers to verify or revoke a new device login */
  REVIEW_LOGIN = "REVIEW_LOGIN",
  /** CRYPTO_INVEST_ONBOARDING - Flow to onboard customers for crypto exchange */
  CRYPTO_INVEST_ONBOARDING = "CRYPTO_INVEST_ONBOARDING",
  /** REQUIREMENT_TEST_FLOW - Requirement Test Flow (productcannon) */
  REQUIREMENT_TEST_FLOW = "REQUIREMENT_TEST_FLOW",
  /** GRANTLY_MANAGED_GRANT - Flow to create a Grant via Grantly. */
  GRANTLY_MANAGED_GRANT = "GRANTLY_MANAGED_GRANT",
  /**
   * CRYPTO_INVEST_CONFIGURE_PAYROLL - [DEPRECATED] Flow to configure payroll to crypto currency automatic conversion AKA Paid In Bitcoin.
   *
   * @deprecated
   */
  CRYPTO_INVEST_CONFIGURE_PAYROLL = "CRYPTO_INVEST_CONFIGURE_PAYROLL",
  /** ACCEPT_GIFT_CARD_PAYMENT - Flow to accept a gift card */
  ACCEPT_GIFT_CARD_PAYMENT = "ACCEPT_GIFT_CARD_PAYMENT",
  /**
   * CANCEL_INVEST_PAYMENT - [DEPRECATED] Flow to cancel an invest payment
   * Stock gifting has been removed from both iOS and Android from app version 4.92 onwards
   *
   * @deprecated
   */
  CANCEL_INVEST_PAYMENT = "CANCEL_INVEST_PAYMENT",
  /** ROUND_UP_ONBOARDING - Flow to onboard to Round Up */
  ROUND_UP_ONBOARDING = "ROUND_UP_ONBOARDING",
  /** CHANGE_ROUND_UP_DESTINATION - Flow to change RoundUp destination */
  CHANGE_ROUND_UP_DESTINATION = "CHANGE_ROUND_UP_DESTINATION",
  /** INITIATE_BANK_ACCOUNT_LINKING - Flow to initiate bank linking */
  INITIATE_BANK_ACCOUNT_LINKING = "INITIATE_BANK_ACCOUNT_LINKING",
  /** POST_SIGN_IN - Flow to gather required post sign-up information during onboarding */
  POST_SIGN_IN = "POST_SIGN_IN",
  /** SERVE_AND_VERIFY_CHALLENGE - Flow to verify a topic (i.e. sms, email) by sending a challenge code */
  SERVE_AND_VERIFY_CHALLENGE = "SERVE_AND_VERIFY_CHALLENGE",
  /** SET_OR_UPDATE_TRUSTED_CONTACT - Flow to set or update a customer's Trusted Contact details. */
  SET_OR_UPDATE_TRUSTED_CONTACT = "SET_OR_UPDATE_TRUSTED_CONTACT",
  /** INITIATE_FIATLY_PAYMENT - Flow to initiate p2p-fiat payment using Fiatly. */
  INITIATE_FIATLY_PAYMENT = "INITIATE_FIATLY_PAYMENT",
  /** ACQUIRE_ALIAS - Flow to add an SMS or email alias to an account */
  ACQUIRE_ALIAS = "ACQUIRE_ALIAS",
  /** FAMILY_ACCOUNT_STATE - Family account state flow to setup family accounts when customer has no sponsorships or show current family members. */
  FAMILY_ACCOUNT_STATE = "FAMILY_ACCOUNT_STATE",
  /**
   * PROVISION_GOOGLE_PAY_IN_APP - Flow to provision customer's card with Google Pay in the app itself
   * (as opposed to the app to app case via GOOGLE_PAY_PROVISIONING)
   */
  PROVISION_GOOGLE_PAY_IN_APP = "PROVISION_GOOGLE_PAY_IN_APP",
  /** PAYMENT_FLOW - Legacy Flow to initiate p2p payment. Now supported by Fiatly. */
  PAYMENT_FLOW = "PAYMENT_FLOW",
  /** PROVISION_APPLE_PAY - Flow to provision customer's Cash card with Apple Pay (both in-app and app-to-app) */
  PROVISION_APPLE_PAY = "PROVISION_APPLE_PAY",
  /** REMOVE_TRUSTED_CONTACT - Flow to remove a customers Trusted Contact. */
  REMOVE_TRUSTED_CONTACT = "REMOVE_TRUSTED_CONTACT",
  /** INITIATE_AFTERPAY_PAYMENT - Flow to initiate an Afterpay payment */
  INITIATE_AFTERPAY_PAYMENT = "INITIATE_AFTERPAY_PAYMENT",
  /** LINK_NEW_PAYMENT_CARD - Flow to link a new card * */
  LINK_NEW_PAYMENT_CARD = "LINK_NEW_PAYMENT_CARD",
  /** CONVERT_GIFT_CARD_TO_CASH - Flow to convert gift card to cash. */
  CONVERT_GIFT_CARD_TO_CASH = "CONVERT_GIFT_CARD_TO_CASH",
  /** NEON_EXAMPLE_GIFT_WRAP - Flow for a Neon exemplar project. */
  NEON_EXAMPLE_GIFT_WRAP = "NEON_EXAMPLE_GIFT_WRAP",
  /** ENABLE_AND_UNMASK_ISSUED_CARD - Enable customer's primary issued card and unmask card numbers. */
  ENABLE_AND_UNMASK_ISSUED_CARD = "ENABLE_AND_UNMASK_ISSUED_CARD",
  /** DIRECT_DEPOSIT_SWITCH_UPSELL - Flow for upsell direct deposit switch. To be used in different contexts: card ordering, activation, etc. */
  DIRECT_DEPOSIT_SWITCH_UPSELL = "DIRECT_DEPOSIT_SWITCH_UPSELL",
  /** GRADUATE_SPONSORSHIP - Graduate from a sponsorship to direct account ownership. */
  GRADUATE_SPONSORSHIP = "GRADUATE_SPONSORSHIP",
  /** VERIFY_PASSCODE_OR_ENABLE_SECURITY_LOCK - Verify a passcode or require the user to enable a security lock if they have not done so */
  VERIFY_PASSCODE_OR_ENABLE_SECURITY_LOCK = "VERIFY_PASSCODE_OR_ENABLE_SECURITY_LOCK",
  /** VERIFY_PASSCODE_IF_SECURITY_LOCK_ENABLED - Verify a passcode if the user has security lock enabled */
  VERIFY_PASSCODE_IF_SECURITY_LOCK_ENABLED = "VERIFY_PASSCODE_IF_SECURITY_LOCK_ENABLED",
  /** CHANGE_AFTERPAY_PAYMENT_DATE - Change upcoming payment date for an Afterpay order */
  CHANGE_AFTERPAY_PAYMENT_DATE = "CHANGE_AFTERPAY_PAYMENT_DATE",
  /** CANCEL_PENDING_ROUND_UP_EXECUTION - Flow to cancel a pending Round Up execution */
  CANCEL_PENDING_ROUND_UP_EXECUTION = "CANCEL_PENDING_ROUND_UP_EXECUTION",
  /** ADD_EMAIL_ALIAS - Flow to add an email alias to a customer's account */
  ADD_EMAIL_ALIAS = "ADD_EMAIL_ALIAS",
  /** ADD_SMS_ALIAS - Flow to add an SMS alias to a customer's account */
  ADD_SMS_ALIAS = "ADD_SMS_ALIAS",
  /** ENABLE_SECURITY_LOCk - Flow to enable a user's security lock */
  ENABLE_SECURITY_LOCk = "ENABLE_SECURITY_LOCk",
  /** DISABLE_SECURITY_LOCK - Flow to disable a user's security lock */
  DISABLE_SECURITY_LOCK = "DISABLE_SECURITY_LOCK",
  /** SET_PASSCODE - Flow to set a passcode or do nothing if it is already set */
  SET_PASSCODE = "SET_PASSCODE",
  /** SET_OR_UPDATE_PASSCODE - Flow to set a passcode, or change it if already set */
  SET_OR_UPDATE_PASSCODE = "SET_OR_UPDATE_PASSCODE",
  /** RESET_PASSCODE - Flow to allow a user to reset their passcode */
  RESET_PASSCODE = "RESET_PASSCODE",
  /**
   * ELIGIBILITY_RESOLUTION - Reserved name for eligibility checks. https://github.com/squareup/cash-eligibility
   * https://www.notion.so/cashappcash/Eligibility-framework-be2496749ea342d3b153e13ccdcfff3a
   */
  ELIGIBILITY_RESOLUTION = "ELIGIBILITY_RESOLUTION",
  INIT_REMIT = "INIT_REMIT",
  /** SEND_GIFT_CARD_PAYMENT - Customer send gift card to one or more customers */
  SEND_GIFT_CARD_PAYMENT = "SEND_GIFT_CARD_PAYMENT",
  /** EXEMPLAR_ORDER_PIZZA - A fake flow for Exemplar code */
  EXEMPLAR_ORDER_PIZZA = "EXEMPLAR_ORDER_PIZZA",
  /** EXEMPLAR_ORDER_PIZZA_WITH_SURVEY - A fake flow for Exemplar code */
  EXEMPLAR_ORDER_PIZZA_WITH_SURVEY = "EXEMPLAR_ORDER_PIZZA_WITH_SURVEY",
  /** EDIT_CUSTOMER_BIO - A new flow to replace EDIT_PROFILE_BIO in cash-customer-profile */
  EDIT_CUSTOMER_BIO = "EDIT_CUSTOMER_BIO",
  /** INVEST_TEEN_REQUEST_AUTHORIZATION - Allows teen user to request investing authorization */
  INVEST_TEEN_REQUEST_AUTHORIZATION = "INVEST_TEEN_REQUEST_AUTHORIZATION",
  /** INVEST_SPONSOR_AUTHORIZATION_APPROVAL - Allows teen sponsor to approve authorization */
  INVEST_SPONSOR_AUTHORIZATION_APPROVAL = "INVEST_SPONSOR_AUTHORIZATION_APPROVAL",
  /** CONFIRM_VERIFIED_IDENTITY - Prompts a customer to confirm their already verified identity */
  CONFIRM_VERIFIED_IDENTITY = "CONFIRM_VERIFIED_IDENTITY",
  /** INITIATE_BITCOIN_WITHDRAWAL - Flow to initiate a bitcoin withdrawal. This may be a lightning or on-chain withdrawal. */
  INITIATE_BITCOIN_WITHDRAWAL = "INITIATE_BITCOIN_WITHDRAWAL",
  /** INITIATE_SPLIT - Initiate a split bill request * */
  INITIATE_SPLIT = "INITIATE_SPLIT",
  /** PAY_BILL - Pay a bill * */
  PAY_BILL = "PAY_BILL",
  CUSTOMER_STRIKE_WARNING = "CUSTOMER_STRIKE_WARNING",
  /** CRYPTO_ONBOARDING - Flow to become eligible to Crypto features * */
  CRYPTO_ONBOARDING = "CRYPTO_ONBOARDING",
  /** SUP_BNPL_CHECKOUT - Flow for single use payment checkout with Afterpay * */
  SUP_BNPL_CHECKOUT = "SUP_BNPL_CHECKOUT",
  /** CREATE_OR_TOGGLE_PASSCODE - Flow for creating a Cash Pin or Toggling on/off Cash Pin * */
  CREATE_OR_TOGGLE_PASSCODE = "CREATE_OR_TOGGLE_PASSCODE",
  /**
   * INVEST_EQUITY_CUSTOMER_ONBOARDING_TRAMPOLINE - DEPRECATED: Flow for enacting the Invest Equity Customer Onboarding as a Franklin subflow.
   *
   * @deprecated
   */
  INVEST_EQUITY_CUSTOMER_ONBOARDING_TRAMPOLINE = "INVEST_EQUITY_CUSTOMER_ONBOARDING_TRAMPOLINE",
  /** INITIATE_OUTBOUND_PHONE_SUPPORT - Flow for initiating a phone support callback request * */
  INITIATE_OUTBOUND_PHONE_SUPPORT = "INITIATE_OUTBOUND_PHONE_SUPPORT",
  /**
   * SOURCE_OF_FUNDS_EMPLOYMENT_TRAMPOLINE - DEPRECATED: Flow for enacting the Source of Funds Employment as a Franklin subflow.
   *
   * @deprecated
   */
  SOURCE_OF_FUNDS_EMPLOYMENT_TRAMPOLINE = "SOURCE_OF_FUNDS_EMPLOYMENT_TRAMPOLINE",
  /**
   * INVEST_WHERE_YOU_SHOP_ONBOARDING - Flow for onboarding onto the Invest-Where-You-Shop feature.
   * This is for the 2022Q3 Financial Services Hackweek
   */
  INVEST_WHERE_YOU_SHOP_ONBOARDING = "INVEST_WHERE_YOU_SHOP_ONBOARDING",
  /** EXEMPLAR_ORDER_PIZZA_VIA_SUBFLOW - A fake flow for Exemplar code */
  EXEMPLAR_ORDER_PIZZA_VIA_SUBFLOW = "EXEMPLAR_ORDER_PIZZA_VIA_SUBFLOW",
  /** EXEMPLAR_ORDER_PIZZA_WITH_IDV - A fake flow for Exemplar code */
  EXEMPLAR_ORDER_PIZZA_WITH_IDV = "EXEMPLAR_ORDER_PIZZA_WITH_IDV",
  /** GET_NON_RECOURSE_PAYOUT - Flow is an alternative to the GET_INSTANT_PAYOUT flow for users who cannot provide payroll data. */
  GET_NON_RECOURSE_PAYOUT = "GET_NON_RECOURSE_PAYOUT",
  /** SET_SAVINGS_GOAL - Set a savings goal as part of savings product. */
  SET_SAVINGS_GOAL = "SET_SAVINGS_GOAL",
  /** SAVINGS_NUX - Flow for onboarding customers to savings product. */
  SAVINGS_NUX = "SAVINGS_NUX",
  /** WIRE_TRANSFER - Flow to display wire transfer details to a customer. */
  WIRE_TRANSFER = "WIRE_TRANSFER",
  /** ORDER_TIGERS - Flow to order tigers via a fictional product, Tigers on Demand. For demo purposes only. */
  ORDER_TIGERS = "ORDER_TIGERS",
  /** SUP_BNPL_CANCEL_CARD - Flow to cancel a generated SUP card for SUP_BNPL_CHECKOUT FLOW * */
  SUP_BNPL_CANCEL_CARD = "SUP_BNPL_CANCEL_CARD",
  /** SUP_BNPL_UPDATE_CARD_AMOUNT - Flow to adjust amount on a generated SUP card for SUP_BNPL_CHECKOUT flow * */
  SUP_BNPL_UPDATE_CARD_AMOUNT = "SUP_BNPL_UPDATE_CARD_AMOUNT",
  /** INVITE_FRIENDS - Flow to invite friends * */
  INVITE_FRIENDS = "INVITE_FRIENDS",
  /** BANK_AND_WIRE_TRANSFERS - This flow will be kicked off if a user has not gone through idv but should get access to Wires. */
  BANK_AND_WIRE_TRANSFERS = "BANK_AND_WIRE_TRANSFERS",
  /** ACCEPT_UPDATED_CARD_TERMS - Flow to display and allow user to accept updated Cash Card terms of service agreement */
  ACCEPT_UPDATED_CARD_TERMS = "ACCEPT_UPDATED_CARD_TERMS",
  /** REFRESH_LIGHTNING_INVOICE - Flow to report on the payment status for a lightning invoice */
  REFRESH_LIGHTNING_INVOICE = "REFRESH_LIGHTNING_INVOICE",
  /**
   * LIMITS_SOURCE_OF_FUNDS_COLLECTION - Flow to collect a customer's source of wealth/funds information for increasing or
   * deceasing their limits
   */
  LIMITS_SOURCE_OF_FUNDS_COLLECTION = "LIMITS_SOURCE_OF_FUNDS_COLLECTION",
  /** SEND_P2P_FIAT_PAYMENT - Sends a p2p fiat payment. */
  SEND_P2P_FIAT_PAYMENT = "SEND_P2P_FIAT_PAYMENT",
  /** CREATE_GROUP - Create a group * */
  CREATE_GROUP = "CREATE_GROUP",
  /** LEAVE_GROUP - Leave a group * */
  LEAVE_GROUP = "LEAVE_GROUP",
  /** CREATE_GROUP_EXPENSE - Create an expense within a group. * */
  CREATE_GROUP_EXPENSE = "CREATE_GROUP_EXPENSE",
  /** FIND_MONEY_RETAILERS - Shows retailers where money can be withdrawn */
  FIND_MONEY_RETAILERS = "FIND_MONEY_RETAILERS",
  /** CRYPTO_SOURCE_OF_FUNDS_COLLECTION - Flow to collect a customer's source of wealth/funds information for crypto onboarding */
  CRYPTO_SOURCE_OF_FUNDS_COLLECTION = "CRYPTO_SOURCE_OF_FUNDS_COLLECTION",
  /** AUTHENTICATE_TRANSACTIONS - Flow to handle potentially unrecognized auth'd transactions and side effects. */
  AUTHENTICATE_TRANSACTIONS = "AUTHENTICATE_TRANSACTIONS",
  /** DOCUMENT_IDENTITY_VERIFICATION - Verify a customer's identity by providing a government id and selfie */
  DOCUMENT_IDENTITY_VERIFICATION = "DOCUMENT_IDENTITY_VERIFICATION",
  /** GROUP_EXPENSE_SLICE_P2P_ACTION - Take a p2p action on an expense slice in a group context * */
  GROUP_EXPENSE_SLICE_P2P_ACTION = "GROUP_EXPENSE_SLICE_P2P_ACTION",
  /** SUP_BNPL_MANAGE_CARD - Flow for managing single-use payment cards (update amount subflow, cancel subflow) * */
  SUP_BNPL_MANAGE_CARD = "SUP_BNPL_MANAGE_CARD",
  /** SANCTIONS_INTERDICTION_INFO_COLLECTION - Flow to collect customer's information when their payment held for sanctions review * */
  SANCTIONS_INTERDICTION_INFO_COLLECTION = "SANCTIONS_INTERDICTION_INFO_COLLECTION",
  /**
   * REVIEW_BUSINESS_ACCOUNT - Deprecated in favor of REVIEW_BUSINESS_ACCOUNT_TAX. This flow was never registered. *
   *
   * @deprecated
   */
  REVIEW_BUSINESS_ACCOUNT = "REVIEW_BUSINESS_ACCOUNT",
  /** UPDATE_BUSINESS_ACCOUNT_TAX_INFO - [DEPRECATED] Flow to collect cash business account customers' tax information. */
  UPDATE_BUSINESS_ACCOUNT_TAX_INFO = "UPDATE_BUSINESS_ACCOUNT_TAX_INFO",
  /** REVIEW_BUSINESS_ACCOUNT_TAX - [DEPRECATED] Flow with information on new tax rules for cash business account customers * */
  REVIEW_BUSINESS_ACCOUNT_TAX = "REVIEW_BUSINESS_ACCOUNT_TAX",
  /** PIGGYBANK_SAVINGS_ROUND_UP - Flow to manage customer savings round up status from savings home * */
  PIGGYBANK_SAVINGS_ROUND_UP = "PIGGYBANK_SAVINGS_ROUND_UP",
  /** ORDER_LIONS - Flow to order lions via a fictional product, Lions on Demand. For demo purposes only. */
  ORDER_LIONS = "ORDER_LIONS",
  /**
   * ELIGIBLE_ORDER_LIONS - Flow to validate order lions eligibility via a fictional product, Lions on Demand.
   * For demo purposes only.
   */
  ELIGIBLE_ORDER_LIONS = "ELIGIBLE_ORDER_LIONS",
  /** DOWNGRADE_BUSINESS_ACCOUNT - Flow for business account downgrade * */
  DOWNGRADE_BUSINESS_ACCOUNT = "DOWNGRADE_BUSINESS_ACCOUNT",
  /** EXEMPLAR_ORDER_PIZZA_MULTI - A fake flow for Exemplar code */
  EXEMPLAR_ORDER_PIZZA_MULTI = "EXEMPLAR_ORDER_PIZZA_MULTI",
  /**
   * INSTANT_PAY_DIRECT_DEPOSIT_SWITCH_OFFER - Instant Paycheck will offer the customer money in exchange for a direct deposit switch to
   * CashApp.  Instant Paycheck will also help them switch via their payroll provider (or manually).
   */
  INSTANT_PAY_DIRECT_DEPOSIT_SWITCH_OFFER = "INSTANT_PAY_DIRECT_DEPOSIT_SWITCH_OFFER",
  /** ORDER_CATS - Flow to order lions via a fictional product, Lions on Demand. For demo purposes only. */
  ORDER_CATS = "ORDER_CATS",
  /**
   * BORROW_UNLOCKED - Borrow will inform the customer that their credit line has been unlocked and allow then
   * to take their first loan.
   */
  BORROW_UNLOCKED = "BORROW_UNLOCKED",
  /**
   * LINK_ACCOUNT_PRE_AUTH - DEPRECATED: This user flow still exists but is now handled as part of the
   * `GRANTLY_MANAGED_GRANT` flow (pre auth requirement) - [GrantFlow](https://github.com/squareup/cash-grantly/blob/main/service/src/main/kotlin/com/squareup/cash/grantly/plasma/GrantFlow.kt).
   * See [LinkAccountPreAuthorizationHandler](https://github.com/squareup/cash-grantly/blob/main/service/src/main/kotlin/com/squareup/cash/grantly/plasma/actionhandlers/linkaccount/LinkAccountPreAuthorizationHandler.kt).
   *
   * Prompt a user to link their Cash account to a third-party account prior to creating
   * a Link Account Grant. This flow should only be triggered as a subflow from within Grantly's
   * Pre-Authorization Requirement Handler.
   *
   * @deprecated
   */
  LINK_ACCOUNT_PRE_AUTH = "LINK_ACCOUNT_PRE_AUTH",
  /** THREE_DOMAIN_SECURE_CHALLENGE - Takes the customer through the steps to resolve a Three Domain Secure Challenge. */
  THREE_DOMAIN_SECURE_CHALLENGE = "THREE_DOMAIN_SECURE_CHALLENGE",
  /**
   * LINK_ACCOUNT_POST_AUTH - DEPRECATED: This user flow still exists but is now handled as part of the
   * `GRANTLY_MANAGED_GRANT` flow (post auth requirement) - [GrantFlow](https://github.com/squareup/cash-grantly/blob/main/service/src/main/kotlin/com/squareup/cash/grantly/plasma/GrantFlow.kt).
   * See [LinkAccountPostAuthorizationHandler](https://github.com/squareup/cash-grantly/blob/main/service/src/main/kotlin/com/squareup/cash/grantly/plasma/actionhandlers/linkaccount/LinkAccountPostAuthorizationHandler.kt).
   *
   * Triggered as a subflow from Grantly's Post-Authorization Requirement Handler.
   * Shows success message to the user.
   *
   * @deprecated
   */
  LINK_ACCOUNT_POST_AUTH = "LINK_ACCOUNT_POST_AUTH",
  /** EXEMPLAR_TREEHOUSE_BLOCKER_PASSTHROUGH - Passthrough flow for sending down a treehouse path */
  EXEMPLAR_TREEHOUSE_BLOCKER_PASSTHROUGH = "EXEMPLAR_TREEHOUSE_BLOCKER_PASSTHROUGH",
  /** DISABLE_RECURRING_EQUITY_BUY - Disables a recurring equity buy */
  DISABLE_RECURRING_EQUITY_BUY = "DISABLE_RECURRING_EQUITY_BUY",
  /**
   * CREATE_OR_EDIT_RECURRING_PAYMENT - Allows a customer to create a new recurring payment between
   * themselves and another cash customer. This flow can also be re-used
   * to allow a customer to edit an existing recurring payment between
   * themselves and  another customer.
   */
  CREATE_OR_EDIT_RECURRING_PAYMENT = "CREATE_OR_EDIT_RECURRING_PAYMENT",
  /** REFUND_GIFT_CARD_PAYMENT - Flow to refund a gift card payment */
  REFUND_GIFT_CARD_PAYMENT = "REFUND_GIFT_CARD_PAYMENT",
  /** LINK_CARD - Flow to link a card */
  LINK_CARD = "LINK_CARD",
  /** SPICE_REQUEST - Flow to request spice from spacing guild (Notary) */
  SPICE_REQUEST = "SPICE_REQUEST",
  /**
   * HAS_SUFFICIENT_FUNDS_OR_LINKED_CARD - Flow to link a card when a customer doesn't have sufficient funds or a linked card for a purchase.
   *
   * @deprecated
   */
  HAS_SUFFICIENT_FUNDS_OR_LINKED_CARD = "HAS_SUFFICIENT_FUNDS_OR_LINKED_CARD",
  /** INITIATE_IN_APP_BROWSER_FOR_OFFER - Enter in-app shopping flow with associated for Offers (i.e. Boosts, CAP Incentive) * */
  INITIATE_IN_APP_BROWSER_FOR_OFFER = "INITIATE_IN_APP_BROWSER_FOR_OFFER",
  /** EXEMPLAR_ORDER_PIZZA_MULTI_VARIANT - A fake flow for Exemplar code */
  EXEMPLAR_ORDER_PIZZA_MULTI_VARIANT = "EXEMPLAR_ORDER_PIZZA_MULTI_VARIANT",
  /** PLASMA_SUBFLOW_WRAPPER - A generic flow for wrapping known Flow as a SubFlow, meant for Client-Driven Flow purposes */
  PLASMA_SUBFLOW_WRAPPER = "PLASMA_SUBFLOW_WRAPPER",
  /** ORDER_RABBITS - Flow to order rabbits via a fictional product, Rabbits on Demand. For demo purposes only. */
  ORDER_RABBITS = "ORDER_RABBITS",
  /** BORROW_UNDERWRITING - Determine if the customer is eligible for a Borrow credit line. */
  BORROW_UNDERWRITING = "BORROW_UNDERWRITING",
  /** PLASMA_REQUIREMENT_WRAPPER - A generic flow for wrapping known Requirement as a SubFlow, meant for Client-Driven Flow purposes */
  PLASMA_REQUIREMENT_WRAPPER = "PLASMA_REQUIREMENT_WRAPPER",
  /** OVERDRAFT - Flow related to Overdraft Upsell for Banking Customers. */
  OVERDRAFT = "OVERDRAFT",
  /** ENABLE_RECURRING_EQUITY_BUY - Enables a recurring equity buy */
  ENABLE_RECURRING_EQUITY_BUY = "ENABLE_RECURRING_EQUITY_BUY",
  /** SUP_CREATE_CARD - Flow to initiate SUP card creation and checkout */
  SUP_CREATE_CARD = "SUP_CREATE_CARD",
  /** SUP_EDIT_CARD - Flow to modify an existing SUP card */
  SUP_EDIT_CARD = "SUP_EDIT_CARD",
  /** SUP_ADJUST_AMOUNT - Flow to adjust the amount on an existing SUP card */
  SUP_ADJUST_AMOUNT = "SUP_ADJUST_AMOUNT",
  /** SUP_CANCEL_CARD - Flow to cancel an existing SUP card */
  SUP_CANCEL_CARD = "SUP_CANCEL_CARD",
  /** CANCEL_P2P_FIAT_PAYMENT - Cancels a p2p fiat payment. */
  CANCEL_P2P_FIAT_PAYMENT = "CANCEL_P2P_FIAT_PAYMENT",
  /** DISABLE_RECURRING_EQUITY_BUY_V2 - Disables a recurring equity buy (Input Proto: DisableRecurringEquityBuyParams) */
  DISABLE_RECURRING_EQUITY_BUY_V2 = "DISABLE_RECURRING_EQUITY_BUY_V2",
  /** IDENTITY_HUB - Flow to initiate Identity Hub */
  IDENTITY_HUB = "IDENTITY_HUB",
  /** OVERDRAFT_ACTIVATION - Overdraft Activation Flow */
  OVERDRAFT_ACTIVATION = "OVERDRAFT_ACTIVATION",
  /**
   * DRAKE_PRESALE_MARCH_2023 - DEPRECATED. Flow for Drake presale event in March 2023
   *
   * @deprecated
   */
  DRAKE_PRESALE_MARCH_2023 = "DRAKE_PRESALE_MARCH_2023",
  /** OVERDRAFT_PAYMENT - Overdraft Re-Payment flow. */
  OVERDRAFT_PAYMENT = "OVERDRAFT_PAYMENT",
  /** RETROACTIVE_FINANCING_INITIATE_LOAN_FLOW - Flow for initiating a loan to retroactively finance a purchase * */
  RETROACTIVE_FINANCING_INITIATE_LOAN_FLOW = "RETROACTIVE_FINANCING_INITIATE_LOAN_FLOW",
  /** REFUND_P2P_FIAT_PAYMENT - Refunds a p2p fiat payment. */
  REFUND_P2P_FIAT_PAYMENT = "REFUND_P2P_FIAT_PAYMENT",
  /** INVEST_EQUITY_CUSTOMER_ONBOARDING - Manages the customer onboarding flow for invest equity */
  INVEST_EQUITY_CUSTOMER_ONBOARDING = "INVEST_EQUITY_CUSTOMER_ONBOARDING",
  /** RETROACTIVE_FINANCING_OVERVIEW_FLOW - Flow for providing overview on the retroactive financing lending product * */
  RETROACTIVE_FINANCING_OVERVIEW_FLOW = "RETROACTIVE_FINANCING_OVERVIEW_FLOW",
  /** LINK_CARD_AUTH - Flow to re-link card with authentication (Example: 3DS authentication) * */
  LINK_CARD_AUTH = "LINK_CARD_AUTH",
  /** EDIT_PAYCHECK_ALLOCATION - Flow to edit a paycheck auto-allocation * */
  EDIT_PAYCHECK_ALLOCATION = "EDIT_PAYCHECK_ALLOCATION",
  /** DISABLE_RECURRING_CRYPTO_BUY - Disable recurring crypto buys (Input Data params: DisableRecurringCryptoBuyParams) */
  DISABLE_RECURRING_CRYPTO_BUY = "DISABLE_RECURRING_CRYPTO_BUY",
  /** DISABLE_RECURRING_CRYPTO_BUY_2 - Disable recurring crypto buys (Input: Query params) */
  DISABLE_RECURRING_CRYPTO_BUY_2 = "DISABLE_RECURRING_CRYPTO_BUY_2",
  /** UPSELL_LINKED_DEBIT - A flow to upsell a customer to link a debit card if one is not currently linked */
  UPSELL_LINKED_DEBIT = "UPSELL_LINKED_DEBIT",
  /** MULTI_APPROVE_SPONSORSHIP - A flow which handles orchestration of a set of pending sponsorships requests */
  MULTI_APPROVE_SPONSORSHIP = "MULTI_APPROVE_SPONSORSHIP",
  /** INITIATE_SEND_BITCOIN - A flow which handles sending bitcoin (e.g. via on-chain or lightning) */
  INITIATE_SEND_BITCOIN = "INITIATE_SEND_BITCOIN",
  /**
   * SKIP_SCHEDULED_RECURRING_PAYMENT - Allows a customer to skip the next scheduled
   *  recurring payment between between them and
   *  another cash customer. Customer who is skipping the scheduled
   *  recurring payment must be the sender in the recurring payment.
   */
  SKIP_SCHEDULED_RECURRING_PAYMENT = "SKIP_SCHEDULED_RECURRING_PAYMENT",
  /**
   * VIEW_PAYMENT_PLAN_FLOW - Flow for viewing the accepted loan's details, upcoming payments, and option to make
   * an early repayment.
   * Currently built for the retroactive financing product, but might be extended to
   * support other products. Also, in the future, this flow might be replaced with a
   * sync entity so that users can view the payment plan offline.
   */
  VIEW_PAYMENT_PLAN_FLOW = "VIEW_PAYMENT_PLAN_FLOW",
  /** RESEND_SPONSORSHIP_FEATURE_INVITATION_REQUEST - A flow to resend a sponsorship feature invitation request. */
  RESEND_SPONSORSHIP_FEATURE_INVITATION_REQUEST = "RESEND_SPONSORSHIP_FEATURE_INVITATION_REQUEST",
  /** APPROVE_DEPENDENT_DISPUTE - A flow for sponsors to approve a dependent's dispute */
  APPROVE_DEPENDENT_DISPUTE = "APPROVE_DEPENDENT_DISPUTE",
  /** REQUIREMENT_WRAPPER - A wrapper flow that just allows a single requirement to be used in a client route - just forwards to a requirement */
  REQUIREMENT_WRAPPER = "REQUIREMENT_WRAPPER",
  /** EDIT_BUSINESS_LOCATION_FLOW - A flow for editing a business location */
  EDIT_BUSINESS_LOCATION_FLOW = "EDIT_BUSINESS_LOCATION_FLOW",
  /** VERIFY_INSTRUMENT_ACCESS_FLOW - Flow to verify that a customer has access to their external instrument for risk purposes */
  VERIFY_INSTRUMENT_ACCESS_FLOW = "VERIFY_INSTRUMENT_ACCESS_FLOW",
  /** CREATE_SECONDARY_PROFILE - Lets a customer create a secondary profile. Prototype only. */
  CREATE_SECONDARY_PROFILE = "CREATE_SECONDARY_PROFILE",
  /** EDIT_BUSINESS_CATEGORY_FLOW - A flow for editing a business category */
  EDIT_BUSINESS_CATEGORY_FLOW = "EDIT_BUSINESS_CATEGORY_FLOW",
  /** EDIT_INSTAGRAM_FOR_C4B_FLOW - A flow for linking instagram to a c4b customer */
  EDIT_INSTAGRAM_FOR_C4B_FLOW = "EDIT_INSTAGRAM_FOR_C4B_FLOW",
  /** DISPUTE_STEP_UP - Asks customer to confirm a transaction dispute that cash has flagged as dispute abuse. */
  DISPUTE_STEP_UP = "DISPUTE_STEP_UP",
  /**
   * GLOBAL_SOURCE_OF_FUNDS_COLLECTION - Flow to collect a customer's source of wealth/funds information for
   * Global compliance work.
   */
  GLOBAL_SOURCE_OF_FUNDS_COLLECTION = "GLOBAL_SOURCE_OF_FUNDS_COLLECTION",
  /**
   * CBCA_CAPSULE_JUNE_2023 - [DEPRECATED] Flow for the Cash by Cash App merch drop, to go along with the Mood Card launch in June 2023
   *
   * @deprecated
   */
  CBCA_CAPSULE_JUNE_2023 = "CBCA_CAPSULE_JUNE_2023",
  /**
   * INSTRUMENT_SELECTION - Flow have the customer choose 1 from a list of instruments
   * 2024-08-19: not used and deprecated
   * https://cash.slack.com/archives/C07839DJJG7/p1723238061704969
   *
   * @deprecated
   */
  INSTRUMENT_SELECTION = "INSTRUMENT_SELECTION",
  /** CARD_ORDER_STATUS - Flow to display customer's cash card order status */
  CARD_ORDER_STATUS = "CARD_ORDER_STATUS",
  /**
   * DISCOVER_CASH_CARD_UPSELL - [DEPRECATED] Flow to initiate Cash Card order from Discovery tab
   *
   * @deprecated
   */
  DISCOVER_CASH_CARD_UPSELL = "DISCOVER_CASH_CARD_UPSELL",
  /** SET_ADDRESS - Flow to add the set address requirement */
  SET_ADDRESS = "SET_ADDRESS",
  /** ACCOUNT_RECOVERY - Flow recovery your account */
  ACCOUNT_RECOVERY = "ACCOUNT_RECOVERY",
  /**
   * INITIATE_MULTICURRENCY_PAYMENT_FOR_REGION - Flow which kicks off the crossborder-2.0 payment experience, used when the recipient is not
   * known, but their region is known
   */
  INITIATE_MULTICURRENCY_PAYMENT_FOR_REGION = "INITIATE_MULTICURRENCY_PAYMENT_FOR_REGION",
  /**
   * INITIATE_MULTICURRENCY_PAYMENT_FOR_RECIPIENT - A flow which kicks off the crossborder-2.0 payment experience, used when the recipient is
   * known
   */
  INITIATE_MULTICURRENCY_PAYMENT_FOR_RECIPIENT = "INITIATE_MULTICURRENCY_PAYMENT_FOR_RECIPIENT",
  /** BORROW_ONBOARDING - Flow to help onboard new Customers to Borrow dependent on their eligibility */
  BORROW_ONBOARDING = "BORROW_ONBOARDING",
  /**
   * INVEST_YOUR_PAYCHECK_CONFIRMATION - This subflow has been replaced by the CONFIRM_SET_PAYCHECK_ALLOCATION requirement.
   * Flow to request confirmation of an Invest Your Paycheck allocation.
   *
   * @deprecated
   */
  INVEST_YOUR_PAYCHECK_CONFIRMATION = "INVEST_YOUR_PAYCHECK_CONFIRMATION",
  /** POSTBANK_RETRIEVE_BANKING_INFORMATION - Flow to retrieve banking information * */
  POSTBANK_RETRIEVE_BANKING_INFORMATION = "POSTBANK_RETRIEVE_BANKING_INFORMATION",
  /** ENABLE_BALANCE_BASED_ADD_CASH - Flow to enable balance based add cash by setting preferences * */
  ENABLE_BALANCE_BASED_ADD_CASH = "ENABLE_BALANCE_BASED_ADD_CASH",
  /** EDIT_BALANCE_BASED_ADD_CASH_MINIMUM_BALANCE_AMOUNT - Flow to edit the balance based add cash minimum amount preference * */
  EDIT_BALANCE_BASED_ADD_CASH_MINIMUM_BALANCE_AMOUNT = "EDIT_BALANCE_BASED_ADD_CASH_MINIMUM_BALANCE_AMOUNT",
  /** EDIT_BALANCE_BASED_ADD_CASH_INCREMENT_AMOUNT - Flow to edit the balance based add cash increment amount preference * */
  EDIT_BALANCE_BASED_ADD_CASH_INCREMENT_AMOUNT = "EDIT_BALANCE_BASED_ADD_CASH_INCREMENT_AMOUNT",
  /** DISABLE_BALANCE_BASED_ADD_CASH - Flow to disable balance based add cash feature * */
  DISABLE_BALANCE_BASED_ADD_CASH = "DISABLE_BALANCE_BASED_ADD_CASH",
  /** INVEST_GRADUATE_SPONSORSHIP - Flow to graduate user from sponsored investing account to individual account */
  INVEST_GRADUATE_SPONSORSHIP = "INVEST_GRADUATE_SPONSORSHIP",
  /** PLAN_TODAY_FLOW - Flow for FakePlasmaClient tests. */
  PLAN_TODAY_FLOW = "PLAN_TODAY_FLOW",
  /** ACCEPT_MULTICURRENCY_QUOTE - A flow during the crossborder-2.0 payment experience, for a sender to accept a exchange rate quote */
  ACCEPT_MULTICURRENCY_QUOTE = "ACCEPT_MULTICURRENCY_QUOTE",
  /**
   * CRYPTO_INVEST_CURRENCY_EXCHANGE_ROW - Flow to initiate exchange for the rest of the world regions (also called BTC100 regions), and for between any two of
   * the three: stablecoin USDC, bitcoin and any fiat currency
   */
  CRYPTO_INVEST_CURRENCY_EXCHANGE_ROW = "CRYPTO_INVEST_CURRENCY_EXCHANGE_ROW",
  /** EXEMPLAR_BLOCKER_SHOWCASE - Example flow for Blocker usage / views */
  EXEMPLAR_BLOCKER_SHOWCASE = "EXEMPLAR_BLOCKER_SHOWCASE",
  /** EXEMPLAR_ORDER_PIZZA_TREEHOUSE_RESUME_TO_PLASMA - Exemplar flow to order pizza from Treehouse and then resume flow to Plasma. */
  EXEMPLAR_ORDER_PIZZA_TREEHOUSE_RESUME_TO_PLASMA = "EXEMPLAR_ORDER_PIZZA_TREEHOUSE_RESUME_TO_PLASMA",
  /** C4B_ONBOARDING - Flow for onboarding (upgrading) to a Cash for Business (C4B) account * */
  C4B_ONBOARDING = "C4B_ONBOARDING",
  /** C4B_KYB - Flow to collect Know Your Business (KYB) information for Cash for Business (C4B) accounts. */
  C4B_KYB = "C4B_KYB",
  /** EDIT_BUSINESS_NAME_FLOW - A flow for collecting business name */
  EDIT_BUSINESS_NAME_FLOW = "EDIT_BUSINESS_NAME_FLOW",
  /** PLASMA_REQUIREMENT_WRAPPER_FOR_FRANKLIN - A generic flow for wrapping known Requirement as a SubFlow, meant for dropping plasma requirements into Franklin * */
  PLASMA_REQUIREMENT_WRAPPER_FOR_FRANKLIN = "PLASMA_REQUIREMENT_WRAPPER_FOR_FRANKLIN",
  PIGGYBANK_SAVINGS_AUTO_SAVE = "PIGGYBANK_SAVINGS_AUTO_SAVE",
  CANCEL_REMIT = "CANCEL_REMIT",
  PIGGYBANK_AUTO_SAVE_ONBOARDING = "PIGGYBANK_AUTO_SAVE_ONBOARDING",
  /** BANK_AND_WIRES_TRANSFER_V2 - Deprecated, Flow for the new wires onboarding experience * */
  BANK_AND_WIRES_TRANSFER_V2 = "BANK_AND_WIRES_TRANSFER_V2",
  /** REVIEW_EXISTING_DISPUTE_REQUEST - Flow for a teen to review an existing dispute request to their sponsor * */
  REVIEW_EXISTING_DISPUTE_REQUEST = "REVIEW_EXISTING_DISPUTE_REQUEST",
  /** WIRE_TRANSFER_VERIFY_IDENTITY - Flow for a customer to complete eIDV, dIDV during wires onboarding * */
  WIRE_TRANSFER_VERIFY_IDENTITY = "WIRE_TRANSFER_VERIFY_IDENTITY",
  /** WIRE_TRANSFER_VIEW_ACCOUNT_DETAILS - Flow to provision a wires account for an eligible customer * */
  WIRE_TRANSFER_VIEW_ACCOUNT_DETAILS = "WIRE_TRANSFER_VIEW_ACCOUNT_DETAILS",
  /** INITIATE_LOAN - New Flow to initiate Borrow Loan * */
  INITIATE_LOAN = "INITIATE_LOAN",
  /** INITIATE_LOAN_PAYMENT - New Flow to initiate Loan Payments * */
  INITIATE_LOAN_PAYMENT = "INITIATE_LOAN_PAYMENT",
  BUSINESS_ACCOUNT_CREATION = "BUSINESS_ACCOUNT_CREATION",
  /** PERSONAL_USE_WARNING - Flow to initiate compliance warning re: business activity in personal accounts * */
  PERSONAL_USE_WARNING = "PERSONAL_USE_WARNING",
  /**
   * MANAGED_CUSTOMER_REQUEST - Creates a `CustomerRequest` public API resource on behalf of a third-party and triggers a
   * `GRANTLY_MANAGED_GRANT` subflow to allow the user to act on the request.
   */
  MANAGED_CUSTOMER_REQUEST = "MANAGED_CUSTOMER_REQUEST",
  /** COLLECT_LEGAL_NAME - Flow to collect legal name outside of onboarding and IDV, used to allow users to 'self report' * */
  COLLECT_LEGAL_NAME = "COLLECT_LEGAL_NAME",
  /** EXEMPLAR_ORDER_PIZZA_VIA_CANCELABLE_SUBFLOW_FLOW - A fake flow for Exemplar code */
  EXEMPLAR_ORDER_PIZZA_VIA_CANCELABLE_SUBFLOW_FLOW = "EXEMPLAR_ORDER_PIZZA_VIA_CANCELABLE_SUBFLOW_FLOW",
  /** PERSONAL_INFORMATION_REFRESH_FLOW - Flow to display customer to review their Personal Info */
  PERSONAL_INFORMATION_REFRESH_FLOW = "PERSONAL_INFORMATION_REFRESH_FLOW",
  /**
   * C4B_KYB_REMEDIATION - Flow to address KYB failed status for Business (C4B) accounts by either downgrading,
   * launching a C4B_KYB subflow to collect KYB data, or simply blocking further actions.
   * The flow adds a C4B_KYB_REMEDIATION_SUBFLOW requirement.
   */
  C4B_KYB_REMEDIATION = "C4B_KYB_REMEDIATION",
  /** BANKING_ADD_BILLER - Flow to add a biller and link a bill in Banking Core's bill feature * */
  BANKING_ADD_BILLER = "BANKING_ADD_BILLER",
  BANKING_PAY_BILL = "BANKING_PAY_BILL",
  /** INITIATE_TAX_UPGRADE_V2 - Overloaded flow to initiate tax upgrade/login */
  INITIATE_TAX_UPGRADE_V2 = "INITIATE_TAX_UPGRADE_V2",
  /**
   * ACCOUNT_RECOVERY_ACCOUNT_IDENTIFICATION_SUBFLOW - Sub flow to start account identification for account recovery. Replaced by LOGIN_ACCOUNT_IDENTIFICATION. *
   *
   * @deprecated
   */
  ACCOUNT_RECOVERY_ACCOUNT_IDENTIFICATION_SUBFLOW = "ACCOUNT_RECOVERY_ACCOUNT_IDENTIFICATION_SUBFLOW",
  /** REVIEW_P2P_REFUND_REQUEST - Flow for customers to review a request from the sender to refund a P2P payment */
  REVIEW_P2P_REFUND_REQUEST = "REVIEW_P2P_REFUND_REQUEST",
  /** CAP_SPLIT_PAYMENT_OWNER - Flow to render Scan & Split Payment owner experience - $Pay * */
  CAP_SPLIT_PAYMENT_OWNER = "CAP_SPLIT_PAYMENT_OWNER",
  /** CAP_SPLIT_PAYMENT_PARTICIPANT - Flow to render Scan & Split Payment participant experience - $Pay * */
  CAP_SPLIT_PAYMENT_PARTICIPANT = "CAP_SPLIT_PAYMENT_PARTICIPANT",
  /** W9_WEBVIEW - Flow to render the W9 Web Form inside a WebView * */
  W9_WEBVIEW = "W9_WEBVIEW",
  /** REFERRAL_PROGRAM - Flow for customers to enter the referral program which can collect a referral code or show current progress * */
  REFERRAL_PROGRAM = "REFERRAL_PROGRAM",
  /**
   * DRAKE_PRESALE_FALL_2023 - [DEPRECATED] Fall 2023 Drake presale event ticket ordering flow
   *
   * @deprecated
   */
  DRAKE_PRESALE_FALL_2023 = "DRAKE_PRESALE_FALL_2023",
  /** PERSONAL_USE_VIOLATION - Flow for flagged users violating personal use policy */
  PERSONAL_USE_VIOLATION = "PERSONAL_USE_VIOLATION",
  /**
   * C4B_SAM_PERSONAL_FEATURE_RESTRICTED - Triggered when a SAM C4B account attempts to use a personal feature, and should be
   * blocked. *
   */
  C4B_SAM_PERSONAL_FEATURE_RESTRICTED = "C4B_SAM_PERSONAL_FEATURE_RESTRICTED",
  /** TIDAL_DISCOUNT_DEC_2023 - Flow for Tidal <> Cash App discount launch Dec 2023 explainer screen and link to Tidal * */
  TIDAL_DISCOUNT_DEC_2023 = "TIDAL_DISCOUNT_DEC_2023",
  /** STANDALONE_DIDV - Flow for standalone dIDV * */
  STANDALONE_DIDV = "STANDALONE_DIDV",
  /** DX_ALPHA_FLOW - Flows added for testing by DX * */
  DX_ALPHA_FLOW = "DX_ALPHA_FLOW",
  DX_BETA_FLOW = "DX_BETA_FLOW",
  /**
   * C4B_KYB_PERSONAL_FEATURE_RESTRICTED_FLOW - Flow to restrict Business (C4B) accounts from accessing certain features (e.g. using Cash Card).
   * The flow adds a C4B_KYB_PERSONAL_FEATURE_RESTRICTED requirement, and may be triggered from in-app banner.
   */
  C4B_KYB_PERSONAL_FEATURE_RESTRICTED_FLOW = "C4B_KYB_PERSONAL_FEATURE_RESTRICTED_FLOW",
  REMIT_NOTIFICATION_MESSAGE = "REMIT_NOTIFICATION_MESSAGE",
  /** DIDV_REFRESH - Flow for customers with verified dIDV status to refresh their expired government Id */
  DIDV_REFRESH = "DIDV_REFRESH",
  /** EXEMPLAR_DUMMY_IDLE_FLOW - A fake flow for Exemplar code */
  EXEMPLAR_DUMMY_IDLE_FLOW = "EXEMPLAR_DUMMY_IDLE_FLOW",
  /** DISPUTE_ADD_EVIDENCE_POST_SUBMISSION - Flow for a customer to add evidence after their claim was submitted */
  DISPUTE_ADD_EVIDENCE_POST_SUBMISSION = "DISPUTE_ADD_EVIDENCE_POST_SUBMISSION",
  /**
   * SPONSORSHIP_INFO_NOTIFICATION - Flow to show single page information screen to sponsors/sponsored customers.
   * Usually linked to a push notification, email link, or activity feed.
   */
  SPONSORSHIP_INFO_NOTIFICATION = "SPONSORSHIP_INFO_NOTIFICATION",
  /** REMIT_SCAM_FLOW - Scam flow for Remittances */
  REMIT_SCAM_FLOW = "REMIT_SCAM_FLOW",
  /** CONFIRM_REQUESTOR_INFORMATION - Flow to show the sponsorship requestor information for sponsorship attestation */
  CONFIRM_REQUESTOR_INFORMATION = "CONFIRM_REQUESTOR_INFORMATION",
  /** CASH_LOCAL_ONBOARDING - Flow for onboarding steps specific to customers referred by Cash App Local. */
  CASH_LOCAL_ONBOARDING = "CASH_LOCAL_ONBOARDING",
  /** BANKING_EBILL_FURTHER_ACTION - Flow to complete further action steps in e-bill enrollment in Banking Core's bill feature * */
  BANKING_EBILL_FURTHER_ACTION = "BANKING_EBILL_FURTHER_ACTION",
  /** BUSINESS_IDV - Flow to complete business identity verification, needed for single memeber LLC and non profits. * */
  BUSINESS_IDV = "BUSINESS_IDV",
  /** REVIEW_RECEIPTS - "Flow to show the customer their receipts * */
  REVIEW_RECEIPTS = "REVIEW_RECEIPTS",
  /** C4B_SQ_ONBOARDING - Flow to migrate C4B customers to Square */
  C4B_SQ_ONBOARDING = "C4B_SQ_ONBOARDING",
  /** CARD_SPENDING_INSIGHTS_NUX - Flow for onboarding customers to Card Spending Insights. */
  CARD_SPENDING_INSIGHTS_NUX = "CARD_SPENDING_INSIGHTS_NUX",
  /** ACQUIRE_CREDIT - Flow that provides pathways to acquiring credit, so that customers can borrow money from Lending. */
  ACQUIRE_CREDIT = "ACQUIRE_CREDIT",
  /** ACCOUNT_RECOVERY_VERIFY_LINKED_CASH_CARD - Flow that confirm if a cash card is linked to a particular customer. */
  ACCOUNT_RECOVERY_VERIFY_LINKED_CASH_CARD = "ACCOUNT_RECOVERY_VERIFY_LINKED_CASH_CARD",
  /** ACCOUNT_RECOVERY_VERIFY_LINKED_DEBIT_CARD - Flow that confirm if a debit card is linked to a particular customer. */
  ACCOUNT_RECOVERY_VERIFY_LINKED_DEBIT_CARD = "ACCOUNT_RECOVERY_VERIFY_LINKED_DEBIT_CARD",
  /** CONFIRM_PATH_TO_BORROW - Subflow that asks customer to confirm their method to acquire Borrow credit. */
  CONFIRM_PATH_TO_BORROW = "CONFIRM_PATH_TO_BORROW",
  /** LINK_BANK_TO_BORROW - Path that may grant access to Borrow credit after reviewing their linked bank data. */
  LINK_BANK_TO_BORROW = "LINK_BANK_TO_BORROW",
  /** DIRECT_DEPOSIT_SWITCH_TO_BORROW - Path that may grant access to Borrow credit after asking them to dd switch and land a paycheck. */
  DIRECT_DEPOSIT_SWITCH_TO_BORROW = "DIRECT_DEPOSIT_SWITCH_TO_BORROW",
  /** CREATE_SECONDARY_PERSONAL_PROFILE - Lets a customer create a personal secondary profile. Prototype only. */
  CREATE_SECONDARY_PERSONAL_PROFILE = "CREATE_SECONDARY_PERSONAL_PROFILE",
  /** RESUME_P2P_FIAT_PAYMENT - Resume a p2p fiat payment. */
  RESUME_P2P_FIAT_PAYMENT = "RESUME_P2P_FIAT_PAYMENT",
  /** BLOCK_UNBLOCK_CARD_MERCHANT - Combined flow for blocking or unblocking a merchant on the customer's Cash Card. */
  BLOCK_UNBLOCK_CARD_MERCHANT = "BLOCK_UNBLOCK_CARD_MERCHANT",
  /** BITCOIN_WITHDRAWAL_ACCEPT_SCAM_WARNING - Subflow that asks a customer to accept the scam warning or cancel the withdrawal. */
  BITCOIN_WITHDRAWAL_ACCEPT_SCAM_WARNING = "BITCOIN_WITHDRAWAL_ACCEPT_SCAM_WARNING",
  /**
   * CASH_LOCAL_SIGNUP_BONUS_PAYOUT - [DEPRECATED] Flow to initiate a Cash Local signup bonus payout. Cash Local stopped driving this in March 2025 and
   * now grants signup bonuses as Local Cash; socialite's handler was then removed, so no service provides this flow
   * type. The value is kept rather than reserved because this module is published. Do not reuse it.
   *
   * @deprecated
   */
  CASH_LOCAL_SIGNUP_BONUS_PAYOUT = "CASH_LOCAL_SIGNUP_BONUS_PAYOUT",
  /** BANKING_REMOVE_BILLER - Flow to remove a biller and unlink a bill in Banking Core's bill feature * */
  BANKING_REMOVE_BILLER = "BANKING_REMOVE_BILLER",
  /** PAYCHECKS_HOME_DIRECT_DEPOSIT - Flow kicked off by direct deposit button on paychecks home screen * */
  PAYCHECKS_HOME_DIRECT_DEPOSIT = "PAYCHECKS_HOME_DIRECT_DEPOSIT",
  /** INSTANT_VIRTUAL_CARD_ACCESS - Flow for Instant Virtual Card access */
  INSTANT_VIRTUAL_CARD_ACCESS = "INSTANT_VIRTUAL_CARD_ACCESS",
  /** UPDATE_ALIAS - Flow that update SMS or Email alias. */
  UPDATE_ALIAS = "UPDATE_ALIAS",
  /** MERCHANT_BLOCK_INFO - Flow to display informational blocker in Blocked Businesses page. */
  MERCHANT_BLOCK_INFO = "MERCHANT_BLOCK_INFO",
  /** DIRECT_DEPOSIT_UPSELL - Flow used for the paychecks feature NUX, may be expanded to other use cases. */
  DIRECT_DEPOSIT_UPSELL = "DIRECT_DEPOSIT_UPSELL",
  /** CHANGE_LOAN_AUTOPAY - Flow that allows a customer to change a Borrow loan's configuration for automatic payments. */
  CHANGE_LOAN_AUTOPAY = "CHANGE_LOAN_AUTOPAY",
  /**
   * SET_BLOCK_STATE - Flow used for blocking/unblocking customers. Also allows blocking on behalf of others
   * if authorized, e.g. sponsors blocking on behalf of dependents.
   */
  SET_BLOCK_STATE = "SET_BLOCK_STATE",
  /** INITIATE_BITCOIN_DEPOSIT_REVERSAL - Flow to initiate a reversal for a rejected Bitcoin deposit */
  INITIATE_BITCOIN_DEPOSIT_REVERSAL = "INITIATE_BITCOIN_DEPOSIT_REVERSAL",
  /** BANKING_DISCONNECT_BILL - Flow to disconnect a bill from Banking Core's bills feature * */
  BANKING_DISCONNECT_BILL = "BANKING_DISCONNECT_BILL",
  /**
   * GRANTLY_SECURITY_LOCK - Flow that prompts users for a passcode confirmation if security lock is enabled for
   * money movement. Triggered as a subflow from Grantly's Pre-Authorization Requirement Handler
   */
  GRANTLY_SECURITY_LOCK = "GRANTLY_SECURITY_LOCK",
  /** BANKING_EDIT_BILL_NICKNAME - Flow to edit a bill's nickname from Banking Core's bills feature * */
  BANKING_EDIT_BILL_NICKNAME = "BANKING_EDIT_BILL_NICKNAME",
  /** C4B_SAM_PERSONAL_ACCOUNT_INTRO - Flow to transition from C4B KYB to SAM personal account creation flows * */
  C4B_SAM_PERSONAL_ACCOUNT_INTRO = "C4B_SAM_PERSONAL_ACCOUNT_INTRO",
  /** PAYCHECKS_APPLET_ONBOARDING - Flow for initial paychecks applet tap * */
  PAYCHECKS_APPLET_ONBOARDING = "PAYCHECKS_APPLET_ONBOARDING",
  /**
   * C4B_TRANSFER_INVEST_EQUITY_ASSETS_FLOW - Flow for transferring equity assets from a Cash Business account to a Personal account as
   * part of the C4B remediation under the Single Account Model (SAM).
   */
  C4B_TRANSFER_INVEST_EQUITY_ASSETS_FLOW = "C4B_TRANSFER_INVEST_EQUITY_ASSETS_FLOW",
  /**
   * LINK_BANK_ACCOUNT_FOR_ENHANCED_VERIFICATION - Wrapper Flow for the Link Bank Account requirement,
   * only used for resolving enhanced verification suspensions
   */
  LINK_BANK_ACCOUNT_FOR_ENHANCED_VERIFICATION = "LINK_BANK_ACCOUNT_FOR_ENHANCED_VERIFICATION",
  /** C4B_FEATURE_RESTRICTION_DOWNGRADE_FLOW - Flow to transition an unsupported business account or failed KYB to a downgrade flow * */
  C4B_FEATURE_RESTRICTION_DOWNGRADE_FLOW = "C4B_FEATURE_RESTRICTION_DOWNGRADE_FLOW",
  /** BANKING_RECONNECT_BILL - Flow to reconnect a bill in Banking Core's bills feature * */
  BANKING_RECONNECT_BILL = "BANKING_RECONNECT_BILL",
  /** REMEDIATE_IDV - Flow to perform remediation setup prior to IDV. Handled in cash-idv. * */
  REMEDIATE_IDV = "REMEDIATE_IDV",
  /**
   * VIEW_AP_PAYMENT_PLAN_FLOW - Flow for viewing an accepted Afterpay loan's details, upcoming payments, and option to make
   * an early repayment.
   */
  VIEW_AP_PAYMENT_PLAN_FLOW = "VIEW_AP_PAYMENT_PLAN_FLOW",
  /**
   * C4B_AUTO_DOWNGRADED - Flow to let customers attest they will no longer use the account for business transactions
   * now that the Cash Business account has been auto-downgraded to a Personal account.
   */
  C4B_AUTO_DOWNGRADED = "C4B_AUTO_DOWNGRADED",
  /**
   * RE_IDV - Flow to let customers re-run their current or updated IDV through our vendors to
   * make sure their IDV is still verified.
   */
  RE_IDV = "RE_IDV",
  /** P2P_SELECT_ISSUE_TYPE - Flow to let customers select type of issue they had with their payment. */
  P2P_SELECT_ISSUE_TYPE = "P2P_SELECT_ISSUE_TYPE",
  /** REVIEW_PERSONAL_INFORMATION - Flow to let customers review and edit their personal information */
  REVIEW_PERSONAL_INFORMATION = "REVIEW_PERSONAL_INFORMATION",
  /** C4B_TTP_ONBOARDING - Flow to let customers onboard to C4B's Tap-to-Pay feature. */
  C4B_TTP_ONBOARDING = "C4B_TTP_ONBOARDING",
  /** JANUS_HAS_PASSCODE - Flow that ensures customer has passcode set. */
  JANUS_HAS_PASSCODE = "JANUS_HAS_PASSCODE",
  /**
   * UPLOAD_DEBIT_CARD_PHOTO_FOR_ENHANCED_VERIFICATION - Wrapper Flow for the Upload Debit Card Photo requirement,
   * only used for resolving enhanced verification suspensions
   */
  UPLOAD_DEBIT_CARD_PHOTO_FOR_ENHANCED_VERIFICATION = "UPLOAD_DEBIT_CARD_PHOTO_FOR_ENHANCED_VERIFICATION",
  /** RAFFLE_BUY - Flow to buy a raffle ticket. Hack Week 2024. */
  RAFFLE_BUY = "RAFFLE_BUY",
  /**
   * PORTFOLIO_PLAYGROUND - HACKWEEK - Flow to allow customers to use the portfolio playground.
   *
   * @deprecated
   */
  PORTFOLIO_PLAYGROUND = "PORTFOLIO_PLAYGROUND",
  /** OAUTH_OIDC - Flow to connect your cash app account to a third party via OAUTH/OIDC. */
  OAUTH_OIDC = "OAUTH_OIDC",
  /** C4B_TTP_RECEIPTS - Flow to allow customers to request C4B Tap-to-Pay receipts. */
  C4B_TTP_RECEIPTS = "C4B_TTP_RECEIPTS",
  /** INPUT_INFO_RE_EIDV - Flow to let customers input information when re-verifying via EIDV */
  INPUT_INFO_RE_EIDV = "INPUT_INFO_RE_EIDV",
  /** EDIT_INFO_RE_EIDV - Flow to let customers edit their information when re-verifying via EIDV */
  EDIT_INFO_RE_EIDV = "EDIT_INFO_RE_EIDV",
  /**
   * LOCAL_TESTING_RE_IDV - Flow ONLY for local testing which calls the RE_IDV flow. This makes it easier to trigger
   * the RE_IDV with intended parameters and verify the response returned.
   */
  LOCAL_TESTING_RE_IDV = "LOCAL_TESTING_RE_IDV",
  /** CASH_LOCAL_ONBOARDING_FROM_IN_APP_UPSELL - Flow for onboarding steps specific to customers referred by Cash App Local through In App Upsell. */
  CASH_LOCAL_ONBOARDING_FROM_IN_APP_UPSELL = "CASH_LOCAL_ONBOARDING_FROM_IN_APP_UPSELL",
  /** OMS_INITIATE_AP_LOAN_PAYMENT_FLOW - Flow for making early loan payment on an Afterpay loan. */
  OMS_INITIATE_AP_LOAN_PAYMENT_FLOW = "OMS_INITIATE_AP_LOAN_PAYMENT_FLOW",
  /** OMS_VIEW_AP_PAYMENT_PLAN_FLOW - TODO: Rename of 347, will remove 347 in a follow up PR */
  OMS_VIEW_AP_PAYMENT_PLAN_FLOW = "OMS_VIEW_AP_PAYMENT_PLAN_FLOW",
  DEPENDENT_P2P_POSSIBLE_UNKNOWN_PEER_CONTEXT = "DEPENDENT_P2P_POSSIBLE_UNKNOWN_PEER_CONTEXT",
  /** P2P_ACTIONABLE_CLAIM - Flow to let customers to complete actionable claim report for P2P payment. */
  P2P_ACTIONABLE_CLAIM = "P2P_ACTIONABLE_CLAIM",
  /** P2P_NON_ACTIONABLE_CLAIM - Flow to let customers to complete non actionable claim report for P2P payment. */
  P2P_NON_ACTIONABLE_CLAIM = "P2P_NON_ACTIONABLE_CLAIM",
  /** LOGIN_V2 - Login flow implemented in janus, this is for franklin migration. */
  LOGIN_V2 = "LOGIN_V2",
  /** CONVERT_STANDARD_CASH_OUT_TO_INSTANT - Flow to allow customers to convert a standard (slow) cash out to an instant one. */
  CONVERT_STANDARD_CASH_OUT_TO_INSTANT = "CONVERT_STANDARD_CASH_OUT_TO_INSTANT",
  /** RE_EIDV - Flow to perform electronic identity verification for Re-IDV. */
  RE_EIDV = "RE_EIDV",
  /** P2P_BAM_APPEAL - Flow used for p2p bam appeals. */
  P2P_BAM_APPEAL = "P2P_BAM_APPEAL",
  /** OMS_VIEW_DOCUMENT_SELECTION_FLOW - Flow for fetching order documents on an order, which can be viewed by the customer via a URL. */
  OMS_VIEW_DOCUMENT_SELECTION_FLOW = "OMS_VIEW_DOCUMENT_SELECTION_FLOW",
  /** DISPLAY_NAME - Flow used for setting display name in Settings */
  DISPLAY_NAME = "DISPLAY_NAME",
  /** CANCEL_SPONSORSHIP - A flow for canceling the current sponsorship */
  CANCEL_SPONSORSHIP = "CANCEL_SPONSORSHIP",
  /** SET_SAVINGS_GOAL_V2 - Set a savings v2 goal as part of savings product. */
  SET_SAVINGS_GOAL_V2 = "SET_SAVINGS_GOAL_V2",
  /** LIVENESS_VERIFICATION - Flow for comparing a customer's previously submitted selfie with a new selfie. */
  LIVENESS_VERIFICATION = "LIVENESS_VERIFICATION",
  /** REQUEST_SPONSOR_LED_SPONSORSHIP - Flow for a parent to request to sponsor a teen's account. */
  REQUEST_SPONSOR_LED_SPONSORSHIP = "REQUEST_SPONSOR_LED_SPONSORSHIP",
  /** APPROVE_SPONSOR_LED_SPONSORSHIP - Flow for a teen to approve a request to sponsor their account. */
  APPROVE_SPONSOR_LED_SPONSORSHIP = "APPROVE_SPONSOR_LED_SPONSORSHIP",
  /** C4B_TTP_REFUND - Flow to allow customers to confirm C4B Tap-to-Pay refund. */
  C4B_TTP_REFUND = "C4B_TTP_REFUND",
  /** INITIATE_BITCOIN_WITHDRAWAL_DOMAIN - Domain-API-driven flow to initiate an on-chain Bitcoin withdrawal. */
  INITIATE_BITCOIN_WITHDRAWAL_DOMAIN = "INITIATE_BITCOIN_WITHDRAWAL_DOMAIN",
  /** C4B_KYB_INVALIDATED_IDV - Allow customers to revalidate their IDV (personal and/or business) post-KYB */
  C4B_KYB_INVALIDATED_IDV = "C4B_KYB_INVALIDATED_IDV",
  /** INVEST_EQUITY_TRADE_BUY - Flow for equity BUY trades */
  INVEST_EQUITY_TRADE_BUY = "INVEST_EQUITY_TRADE_BUY",
  /** INVEST_EQUITY_TRADE_SELL - Flow for equity SELL trades */
  INVEST_EQUITY_TRADE_SELL = "INVEST_EQUITY_TRADE_SELL",
  /** TRANSFER_CASHTAG - Prompt the user to transfer their cashtag to their secondary business account */
  TRANSFER_CASHTAG = "TRANSFER_CASHTAG",
  /** ENABLE_SECURITY_LOCK_FOR_MOVE_MONEY - Flow to enable user's security lock for move money */
  ENABLE_SECURITY_LOCK_FOR_MOVE_MONEY = "ENABLE_SECURITY_LOCK_FOR_MOVE_MONEY",
  /** AFTERPAY_CARD_FINANCE_FEE - Flow to view Afterpay Card Finance Fee */
  AFTERPAY_CARD_FINANCE_FEE = "AFTERPAY_CARD_FINANCE_FEE",
  /** LENDING_IDENTITY_RE_VERIFICATION - Flow to let Lending Customers reverify their Identity */
  LENDING_IDENTITY_RE_VERIFICATION = "LENDING_IDENTITY_RE_VERIFICATION",
  /** CASH_LOCAL_LINK_DEBIT_EDUCATION - Flow to educate customers in cash local and how to link their debit card for cash app pay to be eligible for cash back. */
  CASH_LOCAL_LINK_DEBIT_EDUCATION = "CASH_LOCAL_LINK_DEBIT_EDUCATION",
  /** UPDATE_SAVINGS_GOAL_V2 - Flow to update a v2 savings goal as part of savings product. */
  UPDATE_SAVINGS_GOAL_V2 = "UPDATE_SAVINGS_GOAL_V2",
  /** REMOVE_SAVINGS_GOAL_V2 - Flow to remove a v2 savings goal as part of savings product. */
  REMOVE_SAVINGS_GOAL_V2 = "REMOVE_SAVINGS_GOAL_V2",
  /** PLASMA_REQUIREMENT_WRAPPER_DEMO - Flow with a UI to allow for services to wrap up a requirement during local testing. */
  PLASMA_REQUIREMENT_WRAPPER_DEMO = "PLASMA_REQUIREMENT_WRAPPER_DEMO",
  /** EXEMPLAR_ORDER_PIZZA_WITH_PARAMETERS - A fake flow for Exemplar code */
  EXEMPLAR_ORDER_PIZZA_WITH_PARAMETERS = "EXEMPLAR_ORDER_PIZZA_WITH_PARAMETERS",
  /** EDIT_PAYCHECK_MULTI_ALLOCATION - Flow to edit multi-select paycheck allocations * */
  EDIT_PAYCHECK_MULTI_ALLOCATION = "EDIT_PAYCHECK_MULTI_ALLOCATION",
  /** LINK_BANK_ACCOUNT - Lynx link a bank account * */
  LINK_BANK_ACCOUNT = "LINK_BANK_ACCOUNT",
  /** LINK_BANK_ACCOUNT_PLAID - Lynx link a bank account via plaid * */
  LINK_BANK_ACCOUNT_PLAID = "LINK_BANK_ACCOUNT_PLAID",
  /** LINK_BANK_ACCOUNT_MANUAL - Lynx link a bank account via manual inputs * */
  LINK_BANK_ACCOUNT_MANUAL = "LINK_BANK_ACCOUNT_MANUAL",
  /** CASH_LOCAL_MINIMAL_ONBOARDING_FLOW - Flow for the minimal set of onboarding steps specific to customers referred by Cash App Local. */
  CASH_LOCAL_MINIMAL_ONBOARDING_FLOW = "CASH_LOCAL_MINIMAL_ONBOARDING_FLOW",
  /** OMS_CHANGE_LOAN_AUTOPAY_FLOW - Flow that allows a customer to change a AP convergence order automatic payments config. */
  OMS_CHANGE_LOAN_AUTOPAY_FLOW = "OMS_CHANGE_LOAN_AUTOPAY_FLOW",
  /** EXEMPLAR_ORDER_PIZZA_CUSTOM_BLOCKER - A fake flow for Exemplar code */
  EXEMPLAR_ORDER_PIZZA_CUSTOM_BLOCKER = "EXEMPLAR_ORDER_PIZZA_CUSTOM_BLOCKER",
  /** INITIATE_CASH_CARD_DISPUTE - Flow to initiate a Cash Card dispute */
  INITIATE_CASH_CARD_DISPUTE = "INITIATE_CASH_CARD_DISPUTE",
  /** AFTERPAY_APPLET_ONBOARDING - Onboarding flow for the Afterpay applet */
  AFTERPAY_APPLET_ONBOARDING = "AFTERPAY_APPLET_ONBOARDING",
  /** AFTERPAY_CARD_AVAILABLE_TO_SPEND - Flow to view Afterpay Card Available to Spend explainer */
  AFTERPAY_CARD_AVAILABLE_TO_SPEND = "AFTERPAY_CARD_AVAILABLE_TO_SPEND",
  /** INITIATE_BITCOIN_DEPOSIT_CONFIRMATION - Flow to confirm the intent to deposit and sell bitcoin */
  INITIATE_BITCOIN_DEPOSIT_CONFIRMATION = "INITIATE_BITCOIN_DEPOSIT_CONFIRMATION",
  /**
   * OMS_INITIATE_LOAN_PAYMENT_FLOW - Wrapper around loan payments for ap and cash, this flow will be used to unify
   * loan payments for both loan providers.
   */
  OMS_INITIATE_LOAN_PAYMENT_FLOW = "OMS_INITIATE_LOAN_PAYMENT_FLOW",
  /** CONFIRM_PHONE_SUPPORT_ATTEMPT_FLOW - Flow to confirm the identity of the customer requesting phone support */
  CONFIRM_PHONE_SUPPORT_ATTEMPT_FLOW = "CONFIRM_PHONE_SUPPORT_ATTEMPT_FLOW",
  /** VIEW_CLAIM_SUBMISSION - Flow to view the submission responses when opening a claim in disputron */
  VIEW_CLAIM_SUBMISSION = "VIEW_CLAIM_SUBMISSION",
  /** APPEALS_SUBMISSION - Flow to let customers start an appeal on their denied dispute. */
  APPEALS_SUBMISSION = "APPEALS_SUBMISSION",
  /** REDIRECT_TEST_FLOW - A test flow. Do not use. */
  REDIRECT_TEST_FLOW = "REDIRECT_TEST_FLOW",
  /** C4B_TTP_PAYMENT_ELIGIBILITY_CHECK - Flow for C4B Tap-to-Pay payment eligibility checks on a customer. */
  C4B_TTP_PAYMENT_ELIGIBILITY_CHECK = "C4B_TTP_PAYMENT_ELIGIBILITY_CHECK",
  /** CASH_LOCAL_COMPLETE_ORDER - Flow to complete a cash local order. */
  CASH_LOCAL_COMPLETE_ORDER = "CASH_LOCAL_COMPLETE_ORDER",
  /**
   * SAVINGS_TRANSFER_FLOW - Flow for savings transfers:
   * https://docs.google.com/document/d/1rdf-VVXM2OGDo5s1fqYZ5jfC5x6vNDB7Hit8mgSaTEA
   */
  SAVINGS_TRANSFER_FLOW = "SAVINGS_TRANSFER_FLOW",
  /**
   * REQUEST_P2P_FIAT_PAYMENT - Request a p2p fiat payment.
   * Represents the flow where a user requests or prompts another user to send a fiat payment.
   * Complementary to SEND_P2P_FIAT_PAYMENT.
   */
  REQUEST_P2P_FIAT_PAYMENT = "REQUEST_P2P_FIAT_PAYMENT",
  /** SAVINGS_UNSUPPORTED_FLOW - Served when a customer is in an unsupported savings state. */
  SAVINGS_UNSUPPORTED_FLOW = "SAVINGS_UNSUPPORTED_FLOW",
  /** VIEW_CLAIM_RESOLUTION_EXPLANATION - Flow to view the explanation of a claim resolution, when opening a claim in disputron */
  VIEW_CLAIM_RESOLUTION_EXPLANATION = "VIEW_CLAIM_RESOLUTION_EXPLANATION",
  /**
   * SET_SCHEDULED_RELOAD - Flow for setting scheduled reload
   * https://www.notion.so/cashappcash/Scheduled-Reloads-Migration-Plan-f114f54c0a7244f599c082c97e15650c
   */
  SET_SCHEDULED_RELOAD = "SET_SCHEDULED_RELOAD",
  /** CRYPTO_INVEST_CHANGE_RECURRING_BUY - Flow for changing a BTC auto-invest schedule. */
  CRYPTO_INVEST_CHANGE_RECURRING_BUY = "CRYPTO_INVEST_CHANGE_RECURRING_BUY",
  /** CHANGE_BITCOIN_WITHDRAWAL_SPEED - Flow to change a Bitcoin withdrawal's speed */
  CHANGE_BITCOIN_WITHDRAWAL_SPEED = "CHANGE_BITCOIN_WITHDRAWAL_SPEED",
  /** REPORT_PROFILE_WITH_DETAILS - Flow to report a profile and provide details. */
  REPORT_PROFILE_WITH_DETAILS = "REPORT_PROFILE_WITH_DETAILS",
  /** INITIATE_BITCOIN_LIGHTNING_WITHDRAWAL_DOMAIN - Domain-API-driven flow to initiate a Bitcoin lightning withdrawal. */
  INITIATE_BITCOIN_LIGHTNING_WITHDRAWAL_DOMAIN = "INITIATE_BITCOIN_LIGHTNING_WITHDRAWAL_DOMAIN",
  /** MANAGE_SAVINGS_ROUND_UPS - Flow to manage round up target for the savings entrypoint */
  MANAGE_SAVINGS_ROUND_UPS = "MANAGE_SAVINGS_ROUND_UPS",
  /** LINK_INSTRUMENT_CUSTOMER_DENYLISTED - Flow to handle denylisted customers during instrument linking */
  LINK_INSTRUMENT_CUSTOMER_DENYLISTED = "LINK_INSTRUMENT_CUSTOMER_DENYLISTED",
  /** FACTOR_VERIFICATION - Flow to power factor verification experience to prove the user has own a certain account. */
  FACTOR_VERIFICATION = "FACTOR_VERIFICATION",
  /** SHOW_CARD_DETAILS - Flow to handle providing secure card details to client */
  SHOW_CARD_DETAILS = "SHOW_CARD_DETAILS",
  /** BITCOIN_WITHDRAWAL_SELF_ATTESTATION - Flow for collecting self-attestation details about a bitcoin withdrawal. */
  BITCOIN_WITHDRAWAL_SELF_ATTESTATION = "BITCOIN_WITHDRAWAL_SELF_ATTESTATION",
  /** ACCOUNT_LINKING - Flow to handle linking two account holders */
  ACCOUNT_LINKING = "ACCOUNT_LINKING",
  /** THREE_DS_AUTHENTICATION - Flow to handle Three DS Authentication */
  THREE_DS_AUTHENTICATION = "THREE_DS_AUTHENTICATION",
  /** BITCOIN_SCAM_PROTECTION - Flow to show scam protection info to elderly customers engaging with Bitcoin for the first time. */
  BITCOIN_SCAM_PROTECTION = "BITCOIN_SCAM_PROTECTION",
  /** DISPLAY_NAME_MIGRATION - Flow allowing a customer to migrate to the new display name standards */
  DISPLAY_NAME_MIGRATION = "DISPLAY_NAME_MIGRATION",
  /** VERIFY_EMAIL - Flow to send and verify an email challenge */
  VERIFY_EMAIL = "VERIFY_EMAIL",
  /** VERIFY_PHONE_NUMBER - Flow to send and verify an sms/phone challenge */
  VERIFY_PHONE_NUMBER = "VERIFY_PHONE_NUMBER",
  /** SPONSOR_ONBOARDING_CONTROLS - Flow to allow sponsors to set dependent controls during sponsorship establishment flows * */
  SPONSOR_ONBOARDING_CONTROLS = "SPONSOR_ONBOARDING_CONTROLS",
  /** ACCOUNT_IDENTIFICATION - Flow to start account identification * */
  ACCOUNT_IDENTIFICATION = "ACCOUNT_IDENTIFICATION",
  /** PREAUTHENTICATED_TRUST_PILOT_REVIEW - Flow to facilitate TrustPilot Reviews via a preauthenticated web view */
  PREAUTHENTICATED_TRUST_PILOT_REVIEW = "PREAUTHENTICATED_TRUST_PILOT_REVIEW",
  /** MONEY_POOL_OWNER_CONTRIBUTION - Flow for a pool owner (aka. organizer) to transfer fiat currency into the pool. */
  MONEY_POOL_OWNER_CONTRIBUTION = "MONEY_POOL_OWNER_CONTRIBUTION",
  /** SEND_POOL_CONTRIBUTION_PAYMENT - Flow for a non-owner (aka. not organizer) to transfer fiat currency into the pool. */
  SEND_POOL_CONTRIBUTION_PAYMENT = "SEND_POOL_CONTRIBUTION_PAYMENT",
  /** ENTER_REFERRAL_CODE - Flow to manually enter a referral code for new customers */
  ENTER_REFERRAL_CODE = "ENTER_REFERRAL_CODE",
  /**
   * ACCESS_ORCHESTRATOR - A wrapper flow to orchestrate onboarding after app launch.
   * This flow will launch login, signup or post sign-up subflows
   */
  ACCESS_ORCHESTRATOR = "ACCESS_ORCHESTRATOR",
  /** SEND_OON_POOL_CONTRIBUTION_PAYMENT - Flow for an out of network (i.e. non-Cash customer) to transfer fiat currency into the pool. */
  SEND_OON_POOL_CONTRIBUTION_PAYMENT = "SEND_OON_POOL_CONTRIBUTION_PAYMENT",
  /** BITCOIN_IDV - Flow for btc customers to complete eIDV and dIDV */
  BITCOIN_IDV = "BITCOIN_IDV",
  /** CLOSE_MONEY_POOL - Flow for money pool owners to close the money pool */
  CLOSE_MONEY_POOL = "CLOSE_MONEY_POOL",
  /** LEAVE_MONEY_POOL - Flow for money pool members to leave the money pool */
  LEAVE_MONEY_POOL = "LEAVE_MONEY_POOL",
  /** REFERRAL_STATUS - Flow to check the status of a pending referral (i.e. progress tracking) */
  REFERRAL_STATUS = "REFERRAL_STATUS",
  /** VERIFY_PASSCODE - Flow to verify passcode */
  VERIFY_PASSCODE = "VERIFY_PASSCODE",
  /** ALIAS_MANAGEMENT - Flow to add or remove aliases for a customer */
  ALIAS_MANAGEMENT = "ALIAS_MANAGEMENT",
  /** BITCOIN_SCAM_INFO - Flow to show more information about bitcoin withdrawals that have been scam blocked * */
  BITCOIN_SCAM_INFO = "BITCOIN_SCAM_INFO",
  /**
   * AUTOMATIONS_EDIT_PAYCHECK_MULTI_ALLOCATION - Flow to wrap around EDIT_PAYCHECK_MULTI_ALLOCATION flow,
   * to perform automations side effects of editing a paycheck allocation
   */
  AUTOMATIONS_EDIT_PAYCHECK_MULTI_ALLOCATION = "AUTOMATIONS_EDIT_PAYCHECK_MULTI_ALLOCATION",
  /** ONBOARDING_ENTER_REFERRAL_CODE - Flow to enter a referral code in onboarding for new customers */
  ONBOARDING_ENTER_REFERRAL_CODE = "ONBOARDING_ENTER_REFERRAL_CODE",
  /**
   * SIGNUP - A wrapper flow for sign up experience.
   * This flow will typically be launched as a subflow from the ACCESS_ORCHESTRATOR flow.
   */
  SIGNUP = "SIGNUP",
  /** INVEST_OPT_OUT_OF_CONVERSION - Flow to opt out of conversion to Apex */
  INVEST_OPT_OUT_OF_CONVERSION = "INVEST_OPT_OUT_OF_CONVERSION",
  /** REPORT_MISSING_BOOST - Flow to report missing boost on card */
  REPORT_MISSING_BOOST = "REPORT_MISSING_BOOST",
  /** VIEW_MONEY_POOL - Flow for money pool members to view the money pool, customer may be asked to upgrade the App in the flow */
  VIEW_MONEY_POOL = "VIEW_MONEY_POOL",
  /** PROMOTION_OPT_IN - Flow to opt in to a promotion */
  PROMOTION_OPT_IN = "PROMOTION_OPT_IN",
  /** INITIATE_CLAIM - Flow to initiate claim (dispute, scam, etc) */
  INITIATE_CLAIM = "INITIATE_CLAIM",
  /** TRANSFER_IN_SETUP_FLOW - Flow for setting up a transfer of funds into a savings balance */
  TRANSFER_IN_SETUP_FLOW = "TRANSFER_IN_SETUP_FLOW",
  /** TRANSFER_OUT_SETUP_FLOW - Flow for setting up a transfer of funds into a savings balance */
  TRANSFER_OUT_SETUP_FLOW = "TRANSFER_OUT_SETUP_FLOW",
  /** TRANSACTION_EDIT_AND_CONFIRM_FLOW - Flow for editing and confirming a savings transfer or recurring transfer */
  TRANSACTION_EDIT_AND_CONFIRM_FLOW = "TRANSACTION_EDIT_AND_CONFIRM_FLOW",
  /** TRANSFER_OPTION_SELECTION_FLOW - Flow for selecting a transfer option, whether to select the source or destination */
  TRANSFER_OPTION_SELECTION_FLOW = "TRANSFER_OPTION_SELECTION_FLOW",
  /** INITIATE_CASH_APP_PAY_CLAIM - Flow to initiate a Cash App Pay claim */
  INITIATE_CASH_APP_PAY_CLAIM = "INITIATE_CASH_APP_PAY_CLAIM",
  /** RESOLVE_B_NOTICE - Flow to resolve a BNotice */
  RESOLVE_B_NOTICE = "RESOLVE_B_NOTICE",
  /** TOGGLE_PREPURCHASE - Flow to toggle on or off pre-purchase cash card financing */
  TOGGLE_PREPURCHASE = "TOGGLE_PREPURCHASE",
  /** ADD_MONEY_TO_SAVINGS_BALANCE - Flow to initiate adding money to a savings balance */
  ADD_MONEY_TO_SAVINGS_BALANCE = "ADD_MONEY_TO_SAVINGS_BALANCE",
  /** WITHDRAW_MONEY_FROM_SAVINGS_BALANCE - Flow to initiate withdrawing money from a savings balance */
  WITHDRAW_MONEY_FROM_SAVINGS_BALANCE = "WITHDRAW_MONEY_FROM_SAVINGS_BALANCE",
  /** DEBIT_FLEX_CARD_ONBOARDING - Flow to onboard customer to Flex Debit CashApp Card */
  DEBIT_FLEX_CARD_ONBOARDING = "DEBIT_FLEX_CARD_ONBOARDING",
  /** PREPURCHASE_UPGRADE - Flow to trigger the card upgrade for pre-purchase financing */
  PREPURCHASE_UPGRADE = "PREPURCHASE_UPGRADE",
  /** HUMAN_CONFIRMATION - If the client is an AI agent, asks the user for confirmation. Otherwise, ends immediately. * */
  HUMAN_CONFIRMATION = "HUMAN_CONFIRMATION",
  /** APPROVE_FAMILY_SAVINGS_YIELD - Flow to approve families savings yield * */
  APPROVE_FAMILY_SAVINGS_YIELD = "APPROVE_FAMILY_SAVINGS_YIELD",
  /** SEND_OON_P2P_PAYMENT - Flow for an out of network (i.e. non-Cash customer) to do p2p payments. */
  SEND_OON_P2P_PAYMENT = "SEND_OON_P2P_PAYMENT",
  /** W9_CONSENT_COLLECTION - Flow for fetching a customer's tax info, displaying it to them, and collecting backup-withholding election + w9 consent from the customer. */
  W9_CONSENT_COLLECTION = "W9_CONSENT_COLLECTION",
  /** MAGIC_LINK - Flow for showing a magic link blocker. */
  MAGIC_LINK = "MAGIC_LINK",
  CASH_OUT_INSTRUMENT_SELECTION_FLOW = "CASH_OUT_INSTRUMENT_SELECTION_FLOW",
  CASH_IN_INSTRUMENT_SELECTION_FLOW = "CASH_IN_INSTRUMENT_SELECTION_FLOW",
  EXEMPLAR_ORDER_PIZZA_WITH_MONEYBOT = "EXEMPLAR_ORDER_PIZZA_WITH_MONEYBOT",
  /** SAVINGS_RESOLVE_B_NOTICE - Flow initiated by the customer through the savings applet to resolve a B-Notice. */
  SAVINGS_RESOLVE_B_NOTICE = "SAVINGS_RESOLVE_B_NOTICE",
  /** SPONSOR_LED_KID_ONBOARDING - Flow for sponsor-led kid onboarding. */
  SPONSOR_LED_KID_ONBOARDING = "SPONSOR_LED_KID_ONBOARDING",
  /** PREPURCHASE_INELIGIBLE - Flow for ineligible prepurchase cash app card customers */
  PREPURCHASE_INELIGIBLE = "PREPURCHASE_INELIGIBLE",
  /** DEVICE_MANAGEMENT - Flow for managing devices associated with the account. */
  DEVICE_MANAGEMENT = "DEVICE_MANAGEMENT",
  /**
   * C4B_LISTING_CREATION_ELIGIBILITY_CHECK - [DEPRECATED] Flow for checking customer's eligibility to creating C4b listings. Its handler was never registered
   * in cash-business-payments, so the type was never dispatched to any service, and the handler has since been
   * removed. The value is kept rather than reserved because this module is published. Do not reuse it.
   *
   * @deprecated
   */
  C4B_LISTING_CREATION_ELIGIBILITY_CHECK = "C4B_LISTING_CREATION_ELIGIBILITY_CHECK",
  /**
   * PROMOTIONS_SPONSOR_AUTHORIZATION_APPROVAL - Flow for a Sponsor to turn on a setting to allow their teen to participate in promotions (i.e. incentive
   * campaigns)
   */
  PROMOTIONS_SPONSOR_AUTHORIZATION_APPROVAL = "PROMOTIONS_SPONSOR_AUTHORIZATION_APPROVAL",
  /**
   * PROMOTIONS_TEEN_AUTHORIZATION_REQUEST_STATUS - Flow for a teen to check on the status of the request they sent to their Sponsor to allow them to participate in
   * promotions (i.e. incentive campaigns)
   */
  PROMOTIONS_TEEN_AUTHORIZATION_REQUEST_STATUS = "PROMOTIONS_TEEN_AUTHORIZATION_REQUEST_STATUS",
  /** C4B_LISTING_PAYMENT - Flow for starting a p2p payment flow to a c4b listing */
  C4B_LISTING_PAYMENT = "C4B_LISTING_PAYMENT",
  /** DENYLIST_APPEAL - Flow for starting an in-app denylist appeal */
  DENYLIST_APPEAL = "DENYLIST_APPEAL",
  /** POOL - Flow for money pool members to view the pool via share link, which is a flow deeplink (e.g. https://cash.app/f/POOL?id=abcd1234). */
  POOL = "POOL",
  /** CANCEL_STANDARD_CASH_OUT - Flow to allow customers to cancel a standard (slow) cash out */
  CANCEL_STANDARD_CASH_OUT = "CANCEL_STANDARD_CASH_OUT",
  WEB_PASSKEY_UPSELL = "WEB_PASSKEY_UPSELL",
  /** ENABLE_SCHEDULED_RELOAD - Flow to enable scheduled reload. */
  ENABLE_SCHEDULED_RELOAD = "ENABLE_SCHEDULED_RELOAD",
  /** UPDATE_SCHEDULED_RELOAD_AMOUNT - Flow to update scheduled reload amount. */
  UPDATE_SCHEDULED_RELOAD_AMOUNT = "UPDATE_SCHEDULED_RELOAD_AMOUNT",
  /** UPDATE_SCHEDULED_RELOAD_FREQUENCY - Flow to update scheduled reload frequency and amount. */
  UPDATE_SCHEDULED_RELOAD_FREQUENCY = "UPDATE_SCHEDULED_RELOAD_FREQUENCY",
  /** CRYPTO_INVEST_CURRENCY_EXCHANGE_SPARKY - Flow to initiate bitcoin exchange from crypto-sparky for USD on LN payments. */
  CRYPTO_INVEST_CURRENCY_EXCHANGE_SPARKY = "CRYPTO_INVEST_CURRENCY_EXCHANGE_SPARKY",
  /**
   * ADD_MONEY_TO_SAVINGS_BALANCE_ALIAS - Flow to initiate adding money to a savings balance.
   * This is an alias of ADD_MONEY_TO_SAVINGS_BALANCE to support alternative flow parameters.
   */
  ADD_MONEY_TO_SAVINGS_BALANCE_ALIAS = "ADD_MONEY_TO_SAVINGS_BALANCE_ALIAS",
  /** P2P_SENDER_CONFIRM_RECIPIENT_PHONE - Flow to confirm recipient phone number to prevent misdirected payments. */
  P2P_SENDER_CONFIRM_RECIPIENT_PHONE = "P2P_SENDER_CONFIRM_RECIPIENT_PHONE",
  /**
   * SEND_OON_P2P_PAYMENT_FOR_CASHTAG - Wrapper flow around SEND_OON_P2P_PAYMENT, accepts the recipient cashtag
   * as a flow parameter instead of customer token.
   */
  SEND_OON_P2P_PAYMENT_FOR_CASHTAG = "SEND_OON_P2P_PAYMENT_FOR_CASHTAG",
  /**
   * VALIDATE_P2P_PAYMENT - This flow is used to validate if a customer is able to send/receive P2P payments.
   * It is generic to different payment orientations and customer roles.
   */
  VALIDATE_P2P_PAYMENT = "VALIDATE_P2P_PAYMENT",
  /**
   * P2P_CREATE_PAYMENT_LINK - This flow is used to create a p2p payment link. It wraps around VALIDATE_P2P_PAYMENT flow,
   * and returns a client route containing a payment link.
   */
  P2P_CREATE_PAYMENT_LINK = "P2P_CREATE_PAYMENT_LINK",
  /**
   * GPT_STORE_CONFIRM_ACTION - This flow will be used by gptstore to confirm actions started by it.
   * It is generic so that we can reuse it to confirm different types of actions
   */
  GPT_STORE_CONFIRM_ACTION = "GPT_STORE_CONFIRM_ACTION",
  /**
   * RECURRING_TRANSACTION - Flow type for recurring transactions:
   * https://docs.google.com/document/d/1LbtHd_GkqwYoktygSVe6Zbr4r1noYWZHlyZlWvY-JO8/edit?tab=t.6ugrto5nbhu#bookmark=id.bbt5eaagn7a3
   */
  RECURRING_TRANSACTION = "RECURRING_TRANSACTION",
  /**
   * RECURRING_TRANSACTION_SETUP - Flow type for recurring transaction setup:
   * https://docs.google.com/document/d/1LbtHd_GkqwYoktygSVe6Zbr4r1noYWZHlyZlWvY-JO8/edit?disco=AAABv4QqQz0
   */
  RECURRING_TRANSACTION_SETUP = "RECURRING_TRANSACTION_SETUP",
  /** DISABLE_SCHEDULED_RELOAD - Flow to disable scheduled reload. */
  DISABLE_SCHEDULED_RELOAD = "DISABLE_SCHEDULED_RELOAD",
  /** RECURRING_EDIT - Flow to edit an existing recurring transaction (e.g., modify amount, frequency, source, or destination). */
  RECURRING_EDIT = "RECURRING_EDIT",
  /** ASSIGN_NEW_CASHTAG - Flow to assign a new cashtag through Cashbot. */
  ASSIGN_NEW_CASHTAG = "ASSIGN_NEW_CASHTAG",
  /** SIGN_OUT_OF_DEVICE - Flow to sign out a customer from a device. */
  SIGN_OUT_OF_DEVICE = "SIGN_OUT_OF_DEVICE",
  /** STABLECOIN_WITHDRAWAL - Flow to send stablecoin to an external wallet */
  STABLECOIN_WITHDRAWAL = "STABLECOIN_WITHDRAWAL",
  /** REDIRECT_STABLECOIN_DEPOSIT - Flow to redirect a rejected stablecoin deposit */
  REDIRECT_STABLECOIN_DEPOSIT = "REDIRECT_STABLECOIN_DEPOSIT",
  /** CANCEL_PENDING_NOTARY_REVIEW_BITCOIN_WITHDRAWAL - Flow to cancel a bitcoin withdrawal that is pending notary review */
  CANCEL_PENDING_NOTARY_REVIEW_BITCOIN_WITHDRAWAL = "CANCEL_PENDING_NOTARY_REVIEW_BITCOIN_WITHDRAWAL",
  /**
   * REQUEST_TAP_TO_PAY_DEVICE - DEPRECATED - use ORDER_PAYMENT_TOKEN_DEVICE for Flow to request tap to pay device
   *
   * @deprecated
   */
  REQUEST_TAP_TO_PAY_DEVICE = "REQUEST_TAP_TO_PAY_DEVICE",
  /** LINK_CARD_TO_MERCHANTS - Flow to link card to merchants via Knot card switcher */
  LINK_CARD_TO_MERCHANTS = "LINK_CARD_TO_MERCHANTS",
  /** CARD_ORDERING_FLOW - Unified flow for all card ordering scenarios (first-time order, design change, replacement, expiration, etc.) */
  CARD_ORDERING_FLOW = "CARD_ORDERING_FLOW",
  /** INVEST_EQUITY_CUSTOMER_ONBOARDING_V2 - V2 of the customer onboarding flow for invest equity */
  INVEST_EQUITY_CUSTOMER_ONBOARDING_V2 = "INVEST_EQUITY_CUSTOMER_ONBOARDING_V2",
  /** INDIVIDUAL_ACCOUNT_ONBOARDING_V2 - Subflow for creating an individual brokerage account */
  INDIVIDUAL_ACCOUNT_ONBOARDING_V2 = "INDIVIDUAL_ACCOUNT_ONBOARDING_V2",
  /** CUSTODIAL_ACCOUNT_ONBOARDING_V2 - Subflow for creating a custodial brokerage account */
  CUSTODIAL_ACCOUNT_ONBOARDING_V2 = "CUSTODIAL_ACCOUNT_ONBOARDING_V2",
  /** REQUEST_PUSH_NOTIFICATIONS - Flow to prompt the customer to enable push notifications */
  REQUEST_PUSH_NOTIFICATIONS = "REQUEST_PUSH_NOTIFICATIONS",
  /** SELECT_PAYMENT_DEVICE - Subflow for the new payment device selection and customization experience. */
  SELECT_PAYMENT_DEVICE = "SELECT_PAYMENT_DEVICE",
  /** LINK_BANK_ACCOUNT_PLAID_THEN_REDIRECT - Flow for redirecting to a client route after doing a plaid bank link */
  LINK_BANK_ACCOUNT_PLAID_THEN_REDIRECT = "LINK_BANK_ACCOUNT_PLAID_THEN_REDIRECT",
  /** TRANSACTION_AUTHENTICATION - Flow for orchestrating between different transaction authentication (3DS, DDS)s* */
  TRANSACTION_AUTHENTICATION = "TRANSACTION_AUTHENTICATION",
  /** CAPITAL_ONE_DDS_AUTHENTICATION - Subflow of TRANSACTION_AUTHENTICATION for running Capital One DDS* */
  CAPITAL_ONE_DDS_AUTHENTICATION = "CAPITAL_ONE_DDS_AUTHENTICATION",
  /** RESTRICTION_HOLDING_PERIOD_INFO - Flow that displays context about a restriction holding period */
  RESTRICTION_HOLDING_PERIOD_INFO = "RESTRICTION_HOLDING_PERIOD_INFO",
  /** RESTRICTION_CANT_SEND_INFO - Flow that displays context about why restricted bitcoin can't be sent */
  RESTRICTION_CANT_SEND_INFO = "RESTRICTION_CANT_SEND_INFO",
  /**
   * CREATE_CARD_DESIGN_V2 - Flow to present available card theme choices, create a card customization,
   * and order the card associated with the customer's chosen theme.
   */
  CREATE_CARD_DESIGN_V2 = "CREATE_CARD_DESIGN_V2",
  /** ORDER_PAYMENT_TOKEN_DEVICE - Flow to order a payment token device * */
  ORDER_PAYMENT_TOKEN_DEVICE = "ORDER_PAYMENT_TOKEN_DEVICE",
  /** PROVISION_PAYMENT_TOKEN_DEVICE - Flow to provision a payment token device * */
  PROVISION_PAYMENT_TOKEN_DEVICE = "PROVISION_PAYMENT_TOKEN_DEVICE",
  /** DEPROVISION_PAYMENT_TOKEN_DEVICE - Flow to deprovision a payment token device * */
  DEPROVISION_PAYMENT_TOKEN_DEVICE = "DEPROVISION_PAYMENT_TOKEN_DEVICE",
  /** P2P_INSUFFICIENT_FUNDS - Flow to handle insufficient funds during P2P payment. */
  P2P_INSUFFICIENT_FUNDS = "P2P_INSUFFICIENT_FUNDS",
  /**
   * VALIDATE_CARD_ORDER_ELIGIBILITY - Subflow to validate customer eligibility for card ordering.
   * Checks if customer has activated physical card, pending orders, or other invalid states
   * before allowing them to proceed with card design/customization.
   */
  VALIDATE_CARD_ORDER_ELIGIBILITY = "VALIDATE_CARD_ORDER_ELIGIBILITY",
  /** SECONDARY_BUSINESS_ACCOUNT_ELIGIBILITY_FLOW - Flow to evaluate eligibility for secondary business account creation with adversity context. */
  SECONDARY_BUSINESS_ACCOUNT_ELIGIBILITY_FLOW = "SECONDARY_BUSINESS_ACCOUNT_ELIGIBILITY_FLOW",
  GET_WALLET_ADDRESSES = "GET_WALLET_ADDRESSES",
  /** LOCK_PAYMENT_TOKEN_DEVICE - Flow to lock a payment token device * */
  LOCK_PAYMENT_TOKEN_DEVICE = "LOCK_PAYMENT_TOKEN_DEVICE",
  /** UNLOCK_PAYMENT_TOKEN_DEVICE - Flow to unlock a payment token device * */
  UNLOCK_PAYMENT_TOKEN_DEVICE = "UNLOCK_PAYMENT_TOKEN_DEVICE",
  /**
   * SUSPENSION_STATE - Flow to prompt customers in any suspension state to complete multiple verification steps
   * for additional data collection before determining what actions should be taken on the
   * customer's account.
   */
  SUSPENSION_STATE = "SUSPENSION_STATE",
  /**
   * ELIGIBILITY_VIOLATION_RESOLUTION - Flow to resolve eligibility violations as Plasma requirements/subflows. This flow only
   * handles violation resolution and does not evaluate eligibility policies.
   */
  ELIGIBILITY_VIOLATION_RESOLUTION = "ELIGIBILITY_VIOLATION_RESOLUTION",
  /** TRUST_HUB_ALERT_POC - POC flow for Trust Hub alert functionality */
  TRUST_HUB_ALERT_POC = "TRUST_HUB_ALERT_POC",
  /** SET_CASHTAG_IN_PROFILE - Flow to set cashtag in profile. */
  SET_CASHTAG_IN_PROFILE = "SET_CASHTAG_IN_PROFILE",
  /** RESUME_POOL_CONTRIBUTION_PAYMENT - Flow for resuming a pool contribution payment. */
  RESUME_POOL_CONTRIBUTION_PAYMENT = "RESUME_POOL_CONTRIBUTION_PAYMENT",
  /** WITHDRAW_CLAIM - Flow to withdraw a claim in disputron */
  WITHDRAW_CLAIM = "WITHDRAW_CLAIM",
  /** P2P_INSTRUMENT_SELECTION - Flow to handle instrument selection for P2P payments. */
  P2P_INSTRUMENT_SELECTION = "P2P_INSTRUMENT_SELECTION",
  STABLECOIN_DEPOSIT_ELIGIBILITY = "STABLECOIN_DEPOSIT_ELIGIBILITY",
  /** SQUARE_STAFF_ONBOARDING - Flow to onboard Square staff members who have been invited to link their Cash account. */
  SQUARE_STAFF_ONBOARDING = "SQUARE_STAFF_ONBOARDING",
  /** SECURITY_LOCK_UPSELL_WITH_PASSCODE_CREATION_SUBFLOW - Subflow for security lock upsell orchestration with passcode creation */
  SECURITY_LOCK_UPSELL_WITH_PASSCODE_CREATION_SUBFLOW = "SECURITY_LOCK_UPSELL_WITH_PASSCODE_CREATION_SUBFLOW",
  /** PHONE_PLAN_ENROLLMENT - Flow for phone plan enrollment (new number or porting). */
  PHONE_PLAN_ENROLLMENT = "PHONE_PLAN_ENROLLMENT",
  /** CASH_SUBSCRIPTION_PAYMENT - Subscription payment authorization via Grantly. */
  CASH_SUBSCRIPTION_PAYMENT = "CASH_SUBSCRIPTION_PAYMENT",
  /** P2P_RECOVERABLE_FUNDING_FAILURE - Flow to handle recoverable funding failures during P2P payment. */
  P2P_RECOVERABLE_FUNDING_FAILURE = "P2P_RECOVERABLE_FUNDING_FAILURE",
  /**
   * PROMOTIONS_SPONSOR_AUTHORIZATION_APPROVAL_ALL_TEENS - Flow for a Sponsor to turn on a setting for *all* their teens to allow them to participate in promotions (i.e. incentive
   * campaigns)
   */
  PROMOTIONS_SPONSOR_AUTHORIZATION_APPROVAL_ALL_TEENS = "PROMOTIONS_SPONSOR_AUTHORIZATION_APPROVAL_ALL_TEENS",
  /** CREATE_JOINT_ACCOUNT - Flow for creating a joint account (experimental) */
  CREATE_JOINT_ACCOUNT = "CREATE_JOINT_ACCOUNT",
  /** CRYPTO_INVEST_CONVERSION_P2P_INFLOWS - Flow to set percentage of P2P inflows to convert to Bitcoin. */
  CRYPTO_INVEST_CONVERSION_P2P_INFLOWS = "CRYPTO_INVEST_CONVERSION_P2P_INFLOWS",
  /**
   * ORDER_PAYMENT_DEVICE - Flow that precedes card and $tag ordering flow. Evaluates eligibility, handles payment device
   * selection and customization, and routes to the correct downstream ordering flow.
   */
  ORDER_PAYMENT_DEVICE = "ORDER_PAYMENT_DEVICE",
  /** EARNER_ONBOARDING_FLOW - Flow for Earner onboarding. */
  EARNER_ONBOARDING_FLOW = "EARNER_ONBOARDING_FLOW",
  /** DENYLIST_APPEAL_VERIFICATION - Flow for denylist appeal verification checks (liveness, eIDV, dIDV) */
  DENYLIST_APPEAL_VERIFICATION = "DENYLIST_APPEAL_VERIFICATION",
  /** PHONE_PLAN_INSTALLATION - Flow for phone plan installation after provisioning. */
  PHONE_PLAN_INSTALLATION = "PHONE_PLAN_INSTALLATION",
  /** PHONE_PLAN_PORT_STATUS - Flow for phone plan port status (pending and declined screens). */
  PHONE_PLAN_PORT_STATUS = "PHONE_PLAN_PORT_STATUS",
  ORDER_PAYMENT_CARD = "ORDER_PAYMENT_CARD",
  /** LOGIN_ACCOUNT_IDENTIFICATION - Stateful subflow to identify account in ACCOUNT_RECOVERY flow. */
  LOGIN_ACCOUNT_IDENTIFICATION = "LOGIN_ACCOUNT_IDENTIFICATION",
  /** CONTEXTUAL_ONBOARDING_AUTH - Contextual onboarding authentication (deep link → authentication). */
  CONTEXTUAL_ONBOARDING_AUTH = "CONTEXTUAL_ONBOARDING_AUTH",
  /** DEBIT_FLEX_VIRTUAL_CARD_ISSUANCE - Subflow for virtual card issuance during debit flex card onboarding. */
  DEBIT_FLEX_VIRTUAL_CARD_ISSUANCE = "DEBIT_FLEX_VIRTUAL_CARD_ISSUANCE",
  /** LOGIN_ACCOUNT_SELECTION - Subflow for login account selection when an account holder has multiple accounts. */
  LOGIN_ACCOUNT_SELECTION = "LOGIN_ACCOUNT_SELECTION",
  /**
   * REPORT_PAYMENT_DEVICE - Unified "Get support" flow for reporting lost, stolen, or damaged payment devices.
   * Owned by whimsicard; delegates device-specific reporting to Taply (tags) and Postcard (cards).
   */
  REPORT_PAYMENT_DEVICE = "REPORT_PAYMENT_DEVICE",
  /**
   * REPORT_PAYMENT_TOKEN_DEVICE - Taply-owned subflow for reporting a payment token device (e.g. Cash App Tag).
   * Handles reason selection, device deactivation, and confirmation screen.
   * Returns ReportPaymentDeviceFlowEndResult with outcome and next action.
   */
  REPORT_PAYMENT_TOKEN_DEVICE = "REPORT_PAYMENT_TOKEN_DEVICE",
  /** PHONE_PLAN_NEW_LINE_STATUS - Flow for phone plan new line status — loading screen while waiting for activation. */
  PHONE_PLAN_NEW_LINE_STATUS = "PHONE_PLAN_NEW_LINE_STATUS",
  /** PHONE_PLAN - Parent flow for phone plans — single entry point that routes to child flows based on DB state. */
  PHONE_PLAN = "PHONE_PLAN",
  /** CASH_LITE_ONBOARDING - Onboarding flow for non-custodial Cash App Lite customers. */
  CASH_LITE_ONBOARDING = "CASH_LITE_ONBOARDING",
  /** CANCEL_RECURRING_PAYMENT - Flow for cancelling a recurring payment with a confirmation screen. */
  CANCEL_RECURRING_PAYMENT = "CANCEL_RECURRING_PAYMENT",
  /** LINKED_BUSINESSES - Flow to list all linked businesses and manage individual ones. Owned by Grantly. */
  LINKED_BUSINESSES = "LINKED_BUSINESSES",
  /** CASH_LITE_KYC - KYC flow for non-custodial Cash App Lite customers — collects identity documents via Persona. */
  CASH_LITE_KYC = "CASH_LITE_KYC",
  /** AUTO_RELOAD_SETUP_BALANCE_BASED_ADD_CASH - Flow to set up balance based add cash auto-reload after card activation * */
  AUTO_RELOAD_SETUP_BALANCE_BASED_ADD_CASH = "AUTO_RELOAD_SETUP_BALANCE_BASED_ADD_CASH",
  /** CREATE_RECURRING_P2P_PAYMENT - Set up the recurring P2P payment schedule. If it begins today, run a sub-flow to dispatch the first payment instantly as well. */
  CREATE_RECURRING_P2P_PAYMENT = "CREATE_RECURRING_P2P_PAYMENT",
  /** PROVIDE_LEGAL_NAME_AND_BIRTHDATE - Collects legal name and birthdate on a single screen. */
  PROVIDE_LEGAL_NAME_AND_BIRTHDATE = "PROVIDE_LEGAL_NAME_AND_BIRTHDATE",
  /** PHONE_PLAN_CANCELLATION - Flow for cancelling a phone plan. */
  PHONE_PLAN_CANCELLATION = "PHONE_PLAN_CANCELLATION",
  /** PHONE_PLAN_RESUME - Flow for resuming a cancelled phone plan. */
  PHONE_PLAN_RESUME = "PHONE_PLAN_RESUME",
  AGENT_CREATION = "AGENT_CREATION",
  /** PHONE_PLAN_DETAILS - Flow for viewing phone plan details and number transfer info. */
  PHONE_PLAN_DETAILS = "PHONE_PLAN_DETAILS",
  /** PHONE_PLAN_PAYMENT - Flow for phone plan payment collection. */
  PHONE_PLAN_PAYMENT = "PHONE_PLAN_PAYMENT",
  /** ADD_PASSKEY - Janus-owned flow that prompts a customer to add a passkey, optionally or as a required step. */
  ADD_PASSKEY = "ADD_PASSKEY",
  /** AUTO_WITHDRAWAL_BITCOIN - Flow for auto-withdraw Bitcoin onboarding and settings management. */
  AUTO_WITHDRAWAL_BITCOIN = "AUTO_WITHDRAWAL_BITCOIN",
  /** ORDER_CASH_LITE_CARD - Order Cash App Lite virtual card flow — handler in cashlite-flow, calls Rain to create user + virtual card. */
  ORDER_CASH_LITE_CARD = "ORDER_CASH_LITE_CARD",
  /** CASH_LITE_SELF_CUSTODIAL_PAYMENT - Cash App Lite (self-custodial) peer-to-peer payment flow. Owned by cashlite-flow. */
  CASH_LITE_SELF_CUSTODIAL_PAYMENT = "CASH_LITE_SELF_CUSTODIAL_PAYMENT",
  /** CASH_LITE_CASH_OUT - Cash App Lite cash-out (off-ramp) flow — sends USDC to a linked Mexican bank account (via SPEI). Owned by cashlite-flow. */
  CASH_LITE_CASH_OUT = "CASH_LITE_CASH_OUT",
  /** LITE_DISABLE_ISSUED_CARD - Disable (lock) a Cash App Lite Rain-issued card. Handler in cashlite-flow, calls Rain to update status. */
  LITE_DISABLE_ISSUED_CARD = "LITE_DISABLE_ISSUED_CARD",
  /** LITE_ENABLE_ISSUED_CARD - Enable (unlock) a Cash App Lite Rain-issued card. Handler in cashlite-flow, calls Rain to update status. */
  LITE_ENABLE_ISSUED_CARD = "LITE_ENABLE_ISSUED_CARD",
  /** CASH_LITE_BITCOIN_WITHDRAWAL - Cash App Lite Bitcoin withdrawal flow. Owned by cashlite-flow. */
  CASH_LITE_BITCOIN_WITHDRAWAL = "CASH_LITE_BITCOIN_WITHDRAWAL",
  /**
   * DATA_SHARING_MANAGEMENT - Flow for the Data Sharing settings menu — lists a customer's active and inactive data
   * sharing grants and lets them inspect or revoke an individual grant. Owned by Grantly.
   */
  DATA_SHARING_MANAGEMENT = "DATA_SHARING_MANAGEMENT",
  /** FINISH_SETUP - Activation "Finish Setup" detail view — step tracker with timeline card list. Owned by referly. */
  FINISH_SETUP = "FINISH_SETUP",
  /** GRADUATE_MANAGED_ACCOUNT - Aegis-owned pre-login flow that graduates an active managed account before sign-in continues. */
  GRADUATE_MANAGED_ACCOUNT = "GRADUATE_MANAGED_ACCOUNT",
  /** REPORT_CASH_LITE_CARD - Report Cash App Lite card flow. Handler in cashlite-flow. */
  REPORT_CASH_LITE_CARD = "REPORT_CASH_LITE_CARD",
  /** CASH_LITE_RECOVERY - Cash App Lite recovery — recovers Turnkey access via an email OTP when a customer has lost their passkey. */
  CASH_LITE_RECOVERY = "CASH_LITE_RECOVERY",
  /** CASH_LITE_GOOGLE_PAY_PUSH_PROVISIONING - Cash App Lite Google Pay provisioning started from Cash App. Handler to be defined in cashlite-flow. */
  CASH_LITE_GOOGLE_PAY_PUSH_PROVISIONING = "CASH_LITE_GOOGLE_PAY_PUSH_PROVISIONING",
  /** CASH_LITE_APPLE_PAY_PUSH_PROVISIONING - Cash App Lite Apple Pay provisioning started from Cash App. Handler to be defined in cashlite-flow. */
  CASH_LITE_APPLE_PAY_PUSH_PROVISIONING = "CASH_LITE_APPLE_PAY_PUSH_PROVISIONING",
  /** CASH_LITE_GOOGLE_PAY_APP_TO_APP_VERIFICATION - Cash App Lite Google Pay app-to-app verification. Handler to be defined in cashlite-flow. */
  CASH_LITE_GOOGLE_PAY_APP_TO_APP_VERIFICATION = "CASH_LITE_GOOGLE_PAY_APP_TO_APP_VERIFICATION",
  /** CASH_LITE_APPLE_PAY_APP_TO_APP_VERIFICATION - Cash App Lite Apple Pay app-to-app verification. Handler to be defined in cashlite-flow. */
  CASH_LITE_APPLE_PAY_APP_TO_APP_VERIFICATION = "CASH_LITE_APPLE_PAY_APP_TO_APP_VERIFICATION",
  /** CASH_LOCAL_CHANGE_GEO - Flow that allows a customer to change their Neighborhoods geo. */
  CASH_LOCAL_CHANGE_GEO = "CASH_LOCAL_CHANGE_GEO",
  /** CUSTOMER_SELF_SERVICE_CANCEL_STANDARD_CASH_OUT - Flow to allow customers to cancel a standard (slow) Cash Out */
  CUSTOMER_SELF_SERVICE_CANCEL_STANDARD_CASH_OUT = "CUSTOMER_SELF_SERVICE_CANCEL_STANDARD_CASH_OUT",
  /** CANCEL_BILL_OR_SUBSCRIPTION - Flow for cancelling a bill or subscription. */
  CANCEL_BILL_OR_SUBSCRIPTION = "CANCEL_BILL_OR_SUBSCRIPTION",
  /** SUPERSTACKING - Superstacking ("Bitcoin Auto Buy") settings flow: enable, edit multiplier, or disable. Owned by crypto-invest-flow. */
  SUPERSTACKING = "SUPERSTACKING",
  /** CASH_LITE_STABLECOIN_WITHDRAWAL - Cash App Lite stablecoin withdrawal to an onchain address. Owned by cashlite-flow. */
  CASH_LITE_STABLECOIN_WITHDRAWAL = "CASH_LITE_STABLECOIN_WITHDRAWAL",
  /** CONNECT_MERCHANT_ACCOUNT_TO_CARD - Flow to connect a merchant account to Cash App Card through the Knot SDK. */
  CONNECT_MERCHANT_ACCOUNT_TO_CARD = "CONNECT_MERCHANT_ACCOUNT_TO_CARD",
  /**
   * BTC_SUBSCRIPTION_TEST_UPSELL - Standalone subscription test upsell flow, launched as a subflow from the buy/sell exchange flow and the
   * on-chain Bitcoin withdrawal flow. Owned by crypto-invest-flow.
   */
  BTC_SUBSCRIPTION_TEST_UPSELL = "BTC_SUBSCRIPTION_TEST_UPSELL",
  /**
   * LITE_ACCOUNT_RECOVERY_INIT - Cash App Lite recovery — client-initiated init leg (phone + SMS + email OTP), janus-owned. Issues the
   * CASH_LITE_RECOVERY session tier at completion, then redirects to LITE_ACCOUNT_RECOVERY.
   */
  LITE_ACCOUNT_RECOVERY_INIT = "LITE_ACCOUNT_RECOVERY_INIT",
  /**
   * LITE_ACCOUNT_RECOVERY - Cash App Lite recovery — passkey re-bind leg, janus-owned. Runs under the CASH_LITE_RECOVERY-tier
   * session from LITE_ACCOUNT_RECOVERY_INIT, re-binds the passkey via ADD_PASSKEY, issues the full CASH_APP_LITE
   * tier, and redirects to POST_SIGN_IN.
   */
  LITE_ACCOUNT_RECOVERY = "LITE_ACCOUNT_RECOVERY",
  /** POST_KID_GRADUATION_RISK_CHECK - Post-kid-graduation NPG risk check during Usher onboarding. Owned by aegis. */
  POST_KID_GRADUATION_RISK_CHECK = "POST_KID_GRADUATION_RISK_CHECK",
  /** CASH_LITE_SWAP - Cash App Lite BTC/USDC swap. Owned by cashlite-flow. */
  CASH_LITE_SWAP = "CASH_LITE_SWAP",
  /**
   * CASH_LOCAL_FOLLOW_TERMS - Neighborhoods follow-terms onboarding screen shown when an unenrolled buyer follows a business. Owned by
   * cash-app-local.
   */
  CASH_LOCAL_FOLLOW_TERMS = "CASH_LOCAL_FOLLOW_TERMS",
  /** CRYPTO_SPARKY_RECEIVE_LIGHTNING_PAYMENTS_AS_DOLLARS - Flow to confirm receiving Lightning payments as dollars. Owned by crypto-sparky. */
  CRYPTO_SPARKY_RECEIVE_LIGHTNING_PAYMENTS_AS_DOLLARS = "CRYPTO_SPARKY_RECEIVE_LIGHTNING_PAYMENTS_AS_DOLLARS",
  /** CASH_LITE_LINK_BANK_ACCOUNT - Cash App Lite bank account linking flow — lets a customer add a SPEI or ACH beneficiary. Owned by cashlite-flow. */
  CASH_LITE_LINK_BANK_ACCOUNT = "CASH_LITE_LINK_BANK_ACCOUNT",
  /**
   * DEACTIVATE_PAYMENT_TOKEN_DEVICE - Remotely deactivates ("pauses") a payment token device (Cash App Tag) with confirmation and success screens —
   * no NFC scan; the device stays re-provisionable. Owned by taply.
   */
  DEACTIVATE_PAYMENT_TOKEN_DEVICE = "DEACTIVATE_PAYMENT_TOKEN_DEVICE",
  /** REFRESH_MERCHANT_SUBSCRIPTIONS - Flow to refresh a merchant's subscriptions through Knot. */
  REFRESH_MERCHANT_SUBSCRIPTIONS = "REFRESH_MERCHANT_SUBSCRIPTIONS",
  /**
   * SHOW_MERCHANT_SUBSCRIPTIONS_NOT_FOUND - Explains that a merchant was linked through Knot but no subscriptions were found for it.
   * Owned by spending-insights.
   */
  SHOW_MERCHANT_SUBSCRIPTIONS_NOT_FOUND = "SHOW_MERCHANT_SUBSCRIPTIONS_NOT_FOUND",
  /**
   * CONNECT_THIRD_PARTY_PARENT_MERCHANT - Offers linking the parent merchant that a subscription is actually paid through (e.g. a
   * Spotify subscription billed via Apple). Owned by spending-insights.
   */
  CONNECT_THIRD_PARTY_PARENT_MERCHANT = "CONNECT_THIRD_PARTY_PARENT_MERCHANT",
  /**
   * BTC_SUBSCRIPTION_INTEREST - Subscription interest confirmation page — shows the customer they are on the waitlist for future bitcoin
   * subscription offers. Deep-linked from a Flack reminder email. Owned by crypto-invest-flow.
   */
  BTC_SUBSCRIPTION_INTEREST = "BTC_SUBSCRIPTION_INTEREST",
  /** EXEMPLAR_PLASMA_GENIE_BLOCKER - Exemplar flow demonstrating Plasma handing UI to a Genie scene and resuming afterward. */
  EXEMPLAR_PLASMA_GENIE_BLOCKER = "EXEMPLAR_PLASMA_GENIE_BLOCKER",
  /** CHECKOUT_RECOVERY - Recovers an ineligible customer before returning to Checkoutly hosted checkout. Owned by Checkoutly. */
  CHECKOUT_RECOVERY = "CHECKOUT_RECOVERY",
  /** CASH_LITE_GROW - Cash App Lite Grow: vault position summary, deposit, and withdraw. Owned by cashlite-flow. */
  CASH_LITE_GROW = "CASH_LITE_GROW",
  /**
   * BTC_HOLDINGS_REWARD_ENROLLMENT - Enrolls a customer in the Bitcoin holdings reward: an intro screen explaining the reward, then enrollment and a
   * success screen. Owned by crypto-invest-flow.
   */
  BTC_HOLDINGS_REWARD_ENROLLMENT = "BTC_HOLDINGS_REWARD_ENROLLMENT",
  /** PURCHASE_GAMING_DIGITAL_GOODS - Purchase of a gaming digital good (e.g. a game currency gift card). Owned by scrip. */
  PURCHASE_GAMING_DIGITAL_GOODS = "PURCHASE_GAMING_DIGITAL_GOODS",
  /** UPDATE_PRIMARY_POSTAL_CODE - Updates a customer's primary postal code. Owned by cash-address. */
  UPDATE_PRIMARY_POSTAL_CODE = "UPDATE_PRIMARY_POSTAL_CODE",
  /**
   * PHONE_PLAN_ENROLLMENT_ROUTER - Phone plan enrollment router — owns the pre-choice screens (booklet, 3 easy steps, email, eSIM check) and the
   * number type selector, then dispatches to one enrollment track. Replaces PHONE_PLAN_ENROLLMENT (544). Owned by
   * cash-subscriptions.
   */
  PHONE_PLAN_ENROLLMENT_ROUTER = "PHONE_PLAN_ENROLLMENT_ROUTER",
  /** PHONE_PLAN_ENROLLMENT_PORTING - Phone plan enrollment track for porting an existing number. Owned by cash-subscriptions. */
  PHONE_PLAN_ENROLLMENT_PORTING = "PHONE_PLAN_ENROLLMENT_PORTING",
  /** PHONE_PLAN_ENROLLMENT_NEW_LINE - Phone plan enrollment track for getting a new number. Owned by cash-subscriptions. */
  PHONE_PLAN_ENROLLMENT_NEW_LINE = "PHONE_PLAN_ENROLLMENT_NEW_LINE",
  /** PHONE_PLAN_ENROLLMENT_TRIAL - Phone plan enrollment track for a free trial on a new number. Owned by cash-subscriptions. */
  PHONE_PLAN_ENROLLMENT_TRIAL = "PHONE_PLAN_ENROLLMENT_TRIAL",
  /** CHECKOUT - Wrapper plasma flow for the Checkout-v2 web flow. Owned by checkoutly. */
  CHECKOUT = "CHECKOUT",
  /** ENHANCED_LIMITS_APPLICATION - Invite-only Enhanced Limits application. Owned by cash-limits-xp. */
  ENHANCED_LIMITS_APPLICATION = "ENHANCED_LIMITS_APPLICATION",
  /** ENHANCED_LIMITS_SOURCE_OF_FUNDS_COLLECTION - Source-of-funds collection subflow for Enhanced Limits. Owned by cash-source-of-funds. */
  ENHANCED_LIMITS_SOURCE_OF_FUNDS_COLLECTION = "ENHANCED_LIMITS_SOURCE_OF_FUNDS_COLLECTION",
  /** GUARDRAILS_ADVERSITY_BLOCKED_ACTION_FLOW - Shows the applicable Guardrails blocker for an action blocked by an adversity. Owned by guardrails. */
  GUARDRAILS_ADVERSITY_BLOCKED_ACTION_FLOW = "GUARDRAILS_ADVERSITY_BLOCKED_ACTION_FLOW",
  /** VALIDATE_ADDRESS - Collects and validates an address without creating a Cash Address record or token. Owned by cash-address. */
  VALIDATE_ADDRESS = "VALIDATE_ADDRESS",
  /** BITCOIN_LOAN_ORIGINATION - Bitcoin loan origination/management/payoff. Owned by knox. */
  BITCOIN_LOAN_ORIGINATION = "BITCOIN_LOAN_ORIGINATION",
  /** UUMM_SOURCE_OF_FUNDS_COLLECTION - Source-of-funds collection subflow for the UUMM suspension. Owned by cash-source-of-funds. */
  UUMM_SOURCE_OF_FUNDS_COLLECTION = "UUMM_SOURCE_OF_FUNDS_COLLECTION",
  /** CASH_LITE_UPDATE_EMAIL - Cash App Lite email update: verify the new email alias, then rotate the wallet email. Owned by cashlite-flow. */
  CASH_LITE_UPDATE_EMAIL = "CASH_LITE_UPDATE_EMAIL",
  /**
   * PHONE_PLAN_STATUS_ROUTER - Phone plan status sub-parent: dispatches to PHONE_PLAN_PORT_STATUS (554) or PHONE_PLAN_NEW_LINE_STATUS (562),
   * both unchanged underneath. Owned by cash-subscriptions.
   */
  PHONE_PLAN_STATUS_ROUTER = "PHONE_PLAN_STATUS_ROUTER",
  /**
   * SET_HYPE_CAMPAIGN_HIDDEN - Hides or un-hides a Cash App Tag hype campaign for the session customer ("Don't show me this again"). Ends
   * without a blocker, confirming with a flow-end toast. Owned by taply.
   */
  SET_HYPE_CAMPAIGN_HIDDEN = "SET_HYPE_CAMPAIGN_HIDDEN",
  /**
   * SET_HYPE_NOTIFICATION_OPT_IN - Opts the session customer in to or out of Cash App Tag hype-drop launch notifications, for one campaign or for
   * every campaign. Ends without a blocker; an opt-in is confirmed with a flow-end toast, an opt-out ends silently.
   * Owned by taply.
   */
  SET_HYPE_NOTIFICATION_OPT_IN = "SET_HYPE_NOTIFICATION_OPT_IN",
  /**
   * VERIFY_PASSCODE_GENIE - Passcode verification for Genie through Plasma while native Genie passcode support is unavailable.
   * Owner: cash-identity-access-eng.
   */
  VERIFY_PASSCODE_GENIE = "VERIFY_PASSCODE_GENIE",
  /** LITE_REVEAL_CARD - Passkey verification for Cash Lite card reveal. Owned by cashlite-flow. */
  LITE_REVEAL_CARD = "LITE_REVEAL_CARD",
}

export enum FlowFlowIntent {
  UNUSED = "UNUSED",
  /**
   * SPECULATIVE - The flow is created without the customer intending to complete it. This flow should not be
   * completable and the flow owner is responsible for ensuring that.
   */
  SPECULATIVE = "SPECULATIVE",
  /** INTENTIONAL - The flow is created with the customer intending to complete the goal. */
  INTENTIONAL = "INTENTIONAL",
}

/**
 * A flow can have flow-specific parameters.  Rather than introducing these parameters as
 * a custom message below, encode them in the data entry as a ByteString.
 *
 * This also contains parameters common across all flows.
 */
export interface FlowParameters {
  add_boost_flow_parameters?: FlowParametersAddBoostFlowParameters | undefined;
  recipient_payment_flow_parameters?: FlowParametersRecipientPaymentFlowParameters | undefined;
  remove_boost_flow_parameters?: FlowParametersRemoveBoostFlowParameters | undefined;
  sender_payment_flow_parameters?: FlowParametersSenderPaymentFlowParameters | undefined;
  disable_issued_card_flow_parameters?: FlowParametersDisableIssuedCardFlowParameters | undefined;
  request_physical_card_flow_parameters?: FlowParametersRequestPhysicalCardFlowParameters | undefined;
  dispute_card_transaction_flow_parameters?: FlowParametersDisputeCardTransactionFlowParameters | undefined;
  activate_physical_card_flow_parameters?: FlowParametersActivatePhysicalCardFlowParameters | undefined;
  activate_direct_deposit_account_flow_parameters?:
    | FlowParametersActivateDirectDepositAccountFlowParameters
    | undefined;
  lock_issued_card_flow_parameters?: FlowParametersLockIssuedCardFlowParameters | undefined;
  change_card_design_flow_parameters?: FlowParametersChangeCardDesignFlowParameters | undefined;
  report_card_lost_and_stolen_flow_parameters?: FlowParametersReportCardLostAndStolenFlowParameters | undefined;
  deposit_paper_cash_flow_parameters?: FlowParametersDepositPaperCashFlowParameters | undefined;
  card_expiration_flow_parameters?: FlowParametersCardExpirationFlowParameters | undefined;
  approve_sponsorship_flow_parameters?: FlowParametersApproveSponsorshipFlowParameters | undefined;
  request_sponsorship_flow_parameters?: FlowParametersRequestSponsorshipFlowParameters | undefined;
  set_or_update_password_parameters?: FlowParametersSetOrUpdatePasswordParameters | undefined;
  set_or_verify_password_parameters?: FlowParametersSetOrVerifyPasswordParameters | undefined;
  accept_gift_card_flow_parameters?: FlowParametersAcceptGiftCardFlowParameters | undefined;
  convert_gift_card_to_cash_parameters?: FlowParametersConvertGiftCardToCashFlowParameters | undefined;
  enable_issued_card_flow_parameters?: FlowParametersEnableIssuedCardFlowParameters | undefined;
  refund_gift_card_flow_parameters?: FlowParametersRefundGiftCardFlowParameters | undefined;
  approve_dependent_request_parameters?: FlowParametersApproveDependentRequestParameters | undefined;
  review_existing_request_parameters?: FlowParametersReviewExistingRequestParameters | undefined;
  known_device_flow_parameters?: FlowParametersKnownDeviceFlowParameters | undefined;
  human_confirmation_parameters?: HumanConfirmationParameters | undefined;
  get_banking_interstitial_flow_parameters?: FlowParametersGetBankingInterstitialFlowParameters | undefined;
  bitcoin_loan_flow_parameters?:
    | FlowParametersBitcoinLoanFlowParameters
    | undefined;
  /** Can hold any list of key/value pairs presented within the query string. */
  query_string_parameters?:
    | FlowParametersQueryStringParameters
    | undefined;
  /** Can hold any proto encoded as a ByteString. */
  data_parameters?:
    | string
    | undefined;
  /** Can hold any proto encoded as a json string. */
  json_parameters?:
    | string
    | undefined;
  /**
   * This field allows the caller to override the theme for all blockers in a given flow.
   *
   * If not set, Plasma will attempt to derive this field from the flow history to ensure
   * that the theme propagates to all blockers.
   */
  theme?: BlockerDescriptorTheme;
  /**
   * Optionally, a flow can have parameters that define when it is considered idle as
   * well as related behavior.
   * See https://go/$devguide/product_velocity/plasma/features/idle_detection/.
   */
  on_idle_parameters?: FlowParametersOnIdleParameters;
  /**
   * True if this flow is sandboxed by its parent. For more info see:
   * https://cash-dev-guide.squarecloudservices.com/product_velocity/plasma/features/cancelable_subflows/
   */
  is_sandboxed?: boolean;
  /**
   * The types of data in these parameters.
   * This will influence who is allowed to view this data in Plasma tooling (like toolbox).
   */
  semantic_types?: SemanticTypes;
  scenario_initiator_type?: ScenarioInitiatorType;
}

export enum FlowParametersAPP_OPERATING_SYSTEM {
  NOT_SET = "NOT_SET",
  ANDROID = "ANDROID",
  IOS = "IOS",
}

export interface FlowParametersAddBoostFlowParameters {
  /** Boost that is being added to the card. */
  boost_token?: string;
  /** Boost that is being replace . */
  selected_reward_token?: string;
  /** Information needed to request a new card for non-cardholders to add a boost */
  card_request_info?: CardRequestInfo;
}

export interface FlowParametersRecipientPaymentFlowParameters {
  payment_token?: string;
  client_id?: string;
  customer_token?: string;
  external_id?: string;
}

export interface FlowParametersRemoveBoostFlowParameters {
  selected_reward_token?: string;
}

export interface FlowParametersSenderPaymentFlowParameters {
  payment_token?: string;
  initiator_client_id?: string;
  initiator_customer_token?: string;
  external_id?: string;
}

export interface FlowParametersDisableIssuedCardFlowParameters {
  app_token?: string;
}

export interface FlowParametersCardExpirationFlowParameters {
  expiration_period?: string;
}

export interface FlowParametersRequestPhysicalCardFlowParameters {
  style_picker_eligible?: boolean;
  metal_card_eligible?: boolean;
  app_version?: string;
  app_operating_system?: FlowParametersAPP_OPERATING_SYSTEM;
  three_dimensional_eligible?: boolean;
  sponsorship_status?: FlowParametersRequestPhysicalCardFlowParametersSponsorshipStatus;
  /** @deprecated */
  browser_interaction?: BrowserInteraction;
  upsell_context?: FlowParametersRequestPhysicalCardFlowParametersUpsellContext;
  external_identifier?: string;
  entrypoint?: FlowParametersRequestPhysicalCardFlowParametersEntrypoint;
  payment_device_config?: FlowParametersRequestPhysicalCardFlowParametersPaymentDeviceConfig;
}

export enum FlowParametersRequestPhysicalCardFlowParametersSponsorshipStatus {
  PENDING = "PENDING",
  SPONSORED = "SPONSORED",
  SUSPENDED = "SUSPENDED",
  UNSPONSORED = "UNSPONSORED",
}

export enum FlowParametersRequestPhysicalCardFlowParametersEntrypoint {
  ENTRYPOINT_UNDEFINED = "ENTRYPOINT_UNDEFINED",
  APP_ONBOARDING = "APP_ONBOARDING",
  /**
   * DIRECT_DEPOSIT_ACCOUNT_SHARING - Ordering a card as a prerequisite for sharing direct deposit account details with an
   * external payroll provider (e.g. Workday). See GetBankingInterstitialFlowParameters.
   */
  DIRECT_DEPOSIT_ACCOUNT_SHARING = "DIRECT_DEPOSIT_ACCOUNT_SHARING",
}

/** Upsell context shown at the confirmation screen of the RequestPhysicalCard flow. */
export interface FlowParametersRequestPhysicalCardFlowParametersUpsellContext {
  title?: string;
  /**
   * description_image_url is deprecated in favor of description_image.
   *
   * @deprecated
   */
  description_image_url?: string;
  description_image?: Image;
  description_text?: string;
  description_subtext?: string;
}

/**
 * Lightweight counterpart of whimsicard's squareup.cash.whimsicard.flows.v1.PaymentDeviceConfig,
 * restricting which payment device types the card design step offers in its device selection
 * grid. Set by entry points that redirect a customer here to order a card as a prerequisite for
 * a tag order, where re-showing the tag would loop. Unset or empty means no restriction.
 */
export interface FlowParametersRequestPhysicalCardFlowParametersPaymentDeviceConfig {
  /**
   * Which payment device types should be shown to the customer. Only the cards-only
   * restriction ([PAYMENT_CARD]) is honored today; other non-empty combinations are ignored
   * until the flow supports them.
   */
  orderable_payment_devices?: FlowParametersRequestPhysicalCardFlowParametersPaymentDeviceConfigPaymentDeviceType[];
}

export enum FlowParametersRequestPhysicalCardFlowParametersPaymentDeviceConfigPaymentDeviceType {
  PAYMENT_DEVICE_TYPE_UNSPECIFIED = "PAYMENT_DEVICE_TYPE_UNSPECIFIED",
  /** PAYMENT_CARD - Cash App Card. */
  PAYMENT_CARD = "PAYMENT_CARD",
  /** PAYMENT_TOKEN_DEVICE - A physical device that uses a digital wallet token for card transactions (e.g. tag). */
  PAYMENT_TOKEN_DEVICE = "PAYMENT_TOKEN_DEVICE",
}

export interface FlowParametersDisputeCardTransactionFlowParameters {
  transaction_token?: string;
  authenticated_with_two_factor?: boolean;
}

export interface FlowParametersActivatePhysicalCardFlowParameters {
  is_card_delievered_within_fourteen_days?: boolean;
  is_local_image_element_supported?: boolean;
  is_form_blocker_supported?: boolean;
  is_android?: boolean;
  browser_interaction?: BrowserInteraction;
  device_id?: string;
}

export interface FlowParametersActivateDirectDepositAccountFlowParameters {
  is_local_image_element_supported?: boolean;
  browser_interaction?: BrowserInteraction;
}

/** Used by both the lock and unlock flows */
export interface FlowParametersLockIssuedCardFlowParameters {
  app_token?: string;
}

export interface FlowParametersChangeCardDesignFlowParameters {
  include_name_blocker?: boolean;
  app_version?: string;
  app_operating_system?: FlowParametersAPP_OPERATING_SYSTEM;
  external_identifier?: string;
}

export interface FlowParametersReportCardLostAndStolenFlowParameters {
  include_name_blocker?: boolean;
  is_new_reorder_supported?: boolean;
  /** deprecated */
  is_ios?: boolean;
  /** deprecated */
  is_android?: boolean;
  app_version?: string;
  app_operating_system?: FlowParametersAPP_OPERATING_SYSTEM;
  is_three_dimensional_eligible?: boolean;
  /** Determines which mode to run this flow in. */
  flow_mode?: FlowParametersReportCardLostAndStolenFlowParametersFlowMode;
  /**
   * Used for Transaction Authentication's replace card subflow.
   * True when more blockers will be presented to the customer by the main flow after the
   * completion of this subflow. False if completion of this subflow will complete the main flow
   * as well.
   */
  subflow_more_blockers_remaining?: boolean;
}

export enum FlowParametersReportCardLostAndStolenFlowParametersFlowMode {
  /**
   * REPORT_CARD_LOST_STOLEN - The original, default flow logic. Allows customer to select whether their card was lost,
   * stolen, or damaged before going through reorder requirements.
   */
  REPORT_CARD_LOST_STOLEN = "REPORT_CARD_LOST_STOLEN",
  /**
   * REPLACE_CARD_SUBFLOW - Treats card as a Risk model suggested replacement before reordering card. Intended to be
   * used a subflow for Transaction Authentication only.
   */
  REPLACE_CARD_SUBFLOW = "REPLACE_CARD_SUBFLOW",
  /**
   * REISSUE_DISPUTED_CARD_SUBFLOW - Reorders the primary card even if it is already terminated. Intended to be used as a
   * subflow within Disputes Flow only.
   */
  REISSUE_DISPUTED_CARD_SUBFLOW = "REISSUE_DISPUTED_CARD_SUBFLOW",
  /**
   * REPLACE_UNDELIVERED_CARD - Reorders the primary card for free, intended for use only for cards that were not delivered
   * to the customer, and never activated. It skips some of the initial screens where the
   * customer would normally select what they want to do.
   */
  REPLACE_UNDELIVERED_CARD = "REPLACE_UNDELIVERED_CARD",
}

export interface FlowParametersDepositPaperCashFlowParameters {
  app_version?: string;
  app_operating_system?: FlowParametersAPP_OPERATING_SYSTEM;
}

export interface FlowParametersApproveSponsorshipFlowParameters {
  sponsored_account_token?: string;
  entry_point?: FlowParametersApproveSponsorshipFlowParametersEntryPoint;
}

export enum FlowParametersApproveSponsorshipFlowParametersEntryPoint {
  ACTIVITY = "ACTIVITY",
  NOTIFICATION = "NOTIFICATION",
  CAMPAIGN = "CAMPAIGN",
  ONBOARDING = "ONBOARDING",
  /** FAMILY_HOME - Sponsors can see pending requests from Family Home */
  FAMILY_HOME = "FAMILY_HOME",
  /** FAMILY_ACCOUNT_STATE_FLOW - Non-sponsors will be redirected to their pending requests from the Family Account State Flow */
  FAMILY_ACCOUNT_STATE_FLOW = "FAMILY_ACCOUNT_STATE_FLOW",
  /** FAMILY_PENDING_REQUESTS_LIST - When a sponsor or non-sponsor has multiple pending requests, they will see them on a list screen */
  FAMILY_PENDING_REQUESTS_LIST = "FAMILY_PENDING_REQUESTS_LIST",
}

export interface FlowParametersRequestSponsorshipFlowParameters {
  full_name?: string;
  birthdate?: BirthdateEndResult;
}

export interface FlowParametersMultiApproveSponsorshipFlowParameters {
  invitation_initiated_sponsored_account_token?: string;
  entry_point?: FlowParametersMultiApproveSponsorshipFlowParametersEntryPoint;
}

export enum FlowParametersMultiApproveSponsorshipFlowParametersEntryPoint {
  ACTIVITY = "ACTIVITY",
  NOTIFICATION = "NOTIFICATION",
  CAMPAIGN = "CAMPAIGN",
  ONBOARDING = "ONBOARDING",
}

export interface FlowParametersSetOrUpdatePasswordParameters {
  /** Allow specifying custom blocker text related to the reason for the password change. */
  create_display_config?: PasswordCreationDisplayConfig;
  verify_display_config?: PasswordVerificationDisplayConfig;
}

export interface FlowParametersSetOrVerifyPasswordParameters {
  /** Allow specifying custom blocker text related to the reason for the password verification. */
  create_display_config?: PasswordCreationDisplayConfig;
  verify_display_config?: PasswordVerificationDisplayConfig;
}

export interface FlowParametersQueryStringParameters {
  /** The query string parameters parsed out of the initiation data. */
  query_parameters_list?: FlowParametersQueryStringParametersParameter[];
  /** The original initiation data string that Plasma received. Useful for debugging. */
  initiation_data?: string;
}

export interface FlowParametersQueryStringParametersParameter {
  key?: string;
  values?: string[];
}

export interface FlowParametersAcceptGiftCardFlowParameters {
  payment_token?: string;
}

export interface FlowParametersConvertGiftCardToCashFlowParameters {
  payment_token?: string;
  gift_card_token?: string;
}

export interface FlowParametersRefundGiftCardFlowParameters {
  payment_token?: string;
}

export interface FlowParametersEnableIssuedCardFlowParameters {
  /** allow unmasking upon unlock */
  unmask?: boolean;
}

/**
 * Specifies how to start a remittance - by providing either a region, a recipient, or a
 * transaction to repeat. If specifying the region or recipient, either a send or receive amount
 * may be specified, but not both.
 * Will be encoded into the data_parameters and used via ClientRoute.
 */
export interface FlowParametersInitRemitFlowParameters {
  target_region?: Region;
  recipient_token?: string;
  send_amount?: Money;
  receive_amount?: Money;
  repeat_transaction_token?: string;
  /**
   * for flows started from the contact or search screens, allows matching an existing recipient
   * or creating a new one with the phone number prefilled.
   */
  recipient_phone_number?: string;
}

/**
 * Any dependent request flow will be provided this payload. This is a shared contract from any product flow
 * that allows for sponsor approval
 */
export interface FlowParametersApproveDependentRequestParameters {
  /** Which dependent requested approval */
  dependent_customer_token?: string;
  /** Aegis' unique identifier for this request */
  product_request_token?: string;
  /**
   * Unique instance ID for the request being approved as provided by the product flow.
   * This is the same ID that was provided to the REQUEST_SPONSOR_APPROVAL requirement
   */
  external_instance_id?: string;
}

/**
 * Any flow for the dependent to review their request will be provided this payload. This is a shared contract from any product flow
 * that allows for depenents to view details about their request for approval
 */
export interface FlowParametersReviewExistingRequestParameters {
  /** Aegis' unique identifier for this request */
  product_request_token?: string;
  /**
   * Unique instance ID for the request being reviewed as provided by the product flow.
   * This is the same ID that was provided initially to the REQUEST_SPONSOR_APPROVAL requirement
   */
  external_instance_id?: string;
}

/**
 * Used for idle detection. For more info see:
 * https://cash-dev-guide.squarecloudservices.com/product_velocity/plasma/features/idle_detection/
 */
export interface FlowParametersOnIdleParameters {
  /** Temporal workflow timer. */
  idle_delay_seconds?: number;
  /**
   * On idle, also call on(Requirement/Flow)Idle of parent that started this Flow as a subflow
   * If no parent exists, do nothing.
   */
  propagate_to_parent?: boolean;
  /** Fail the request to start/end the flow if the Temporal request fails. */
  fail_request_on_error?: boolean;
}

/**
 * Used to determine if a device is a "known" device for the account recovery flow
 * Used for any authentication flow that differentiates experiences based on if a device
 * is known or not.
 */
export interface FlowParametersKnownDeviceFlowParameters {
  backup_tag?: string;
}

/**
 * Parameters for the GET_BANKING_INTERSTITIAL flow, which onboards a customer into banking
 * (IDV + Cash Card order) when a feature requires a banking product they don't have yet.
 */
export interface FlowParametersGetBankingInterstitialFlowParameters {
  entrypoint?: FlowParametersGetBankingInterstitialFlowParametersEntrypoint;
}

export enum FlowParametersGetBankingInterstitialFlowParametersEntrypoint {
  ENTRYPOINT_UNDEFINED = "ENTRYPOINT_UNDEFINED",
  /**
   * DIRECT_DEPOSIT_ACCOUNT_SHARING - The customer arrived from the direct-deposit-account-sharing enrollment flow, where an
   * external payroll provider (e.g. Workday) sends them to Cash App to share their direct
   * deposit account details. Forwarded to downstream subflows (e.g. REQUEST_PHYSICAL_CARD)
   * so they can tailor behavior for customers who came specifically to get a DDA.
   */
  DIRECT_DEPOSIT_ACCOUNT_SHARING = "DIRECT_DEPOSIT_ACCOUNT_SHARING",
}

/**
 * Parameters for the BITCOIN_LOAN_ORIGINATION flow. Absent parameters mean the server decides between
 * origination and payoff from the customer's loan state. Owned by knox.
 */
export interface FlowParametersBitcoinLoanFlowParameters {
  intent?: FlowParametersBitcoinLoanFlowParametersIntent;
}

export enum FlowParametersBitcoinLoanFlowParametersIntent {
  /** INTENT_UNSPECIFIED - No client intent: the server decides between origination and payoff from the customer's loan state. */
  INTENT_UNSPECIFIED = "INTENT_UNSPECIFIED",
  /** ADD_COLLATERAL - The customer chose Add bitcoin on the loan's Manage screen: pledge more collateral onto the active loan. */
  ADD_COLLATERAL = "ADD_COLLATERAL",
  /**
   * PAYOFF - The customer chose Repay loan on the Manage screen: launch payoff for the active loan. Reserved; clients send
   * it once the payoff entry point ships.
   */
  PAYOFF = "PAYOFF",
  /** ORIGINATION - The customer chose to "Get Started" on originating a collateralized bitcoin loan. */
  ORIGINATION = "ORIGINATION",
}
